// src/pages/FullReportPage.jsx  — Part 1 of 4 (imports, helpers, sub-components)
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { useLanguage } from '../hooks/useLanguage';
import { tWithVars } from '../i18n/translations';
import { TESTIMONIAL_BANK } from '../data/testimonials';
import {
  Copy, Check, ChevronDown, ChevronUp, Download, ArrowLeft,
  CheckCircle2, Clock, ExternalLink, Loader2, Sparkles, TrendingUp,
  AlertTriangle, DollarSign, ListTodo, Share2, Star, BarChart, Globe,
  Calendar, Zap, Target, Award, Briefcase, LineChart,
  Lightbulb, BookOpen, Rocket, BrainCircuit, Bot, Workflow, PieChart,
  Gauge, FileText, MessageSquare, Mail, Settings, Database, Lock, Layers,
  ArrowRightCircle, HelpCircle, ThumbsUp, Bookmark, TrendingDown, Percent,
  Timer, Flame, ShieldAlert, AlertOctagon, ChevronRight, ChevronLeft,
  UserCheck, UserPlus, Home, Building, Store, Coffee, HeartPulse,
  Stethoscope, GraduationCap, Factory, Landmark, Wrench, Truck, ShoppingBag,
  Utensils, Hotel, BriefcaseBusiness, Cpu, Globe2, Brain, XCircle, Info, Send,
  RotateCcw, Trophy,
} from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';

import {
  sanitize, formatCurrency, formatPercent, getIndustryBenchmark,
  getAlternativeEdge, edgeColorClasses, iconColorClasses, buildSetupPrompt,
  formatIndustryLabel, possessive,
} from '../utils/reportHelpers';

// ── Tab Components (pre-built, previously unused) ─────────────────────────────
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { GuideTab } from '../components/reports/GuideTab';
import { ToolsTab } from '../components/reports/ToolsTab';
import { DiagnosisTab } from '../components/reports/DiagnosisTab';
import { FinancialsTab } from '../components/reports/FinancialsTab';
import PromptsTab from '../components/reports/PromptsTab';
import PlaybooksTab from '../components/reports/PlaybooksTab';
import { MarketTab } from '../components/reports/MarketTab';
import { RisksTab } from '../components/reports/RisksTab';
import { ComplianceTab } from '../components/reports/ComplianceTab';
import { CompetitiveTab } from '../components/reports/CompetitiveTab';
// Removed: GovernanceTab (merged compliance warnings into RisksTab)
// Removed: ChecklistTab (redundant with GuideTab's 7-day plan + phased roadmap)
// Removed: DepartmentsTab (enterprise framing doesn't fit SMB product)

const API_BASE =
  process.env.REACT_APP_BACKEND_URL ||
  'https://ai-workshop-llc-backend.onrender.com';

// ─── Tool URL Directory ────────────────────────────────────────────────────────
const TOOL_URL_MAP = {
  shopify: 'https://www.shopify.com', shopify_ai: 'https://www.shopify.com/magic',
  chatgpt: 'https://chat.openai.com', claude: 'https://claude.ai',
  gemini: 'https://gemini.google.com', grok: 'https://grok.x.ai',
  zapier: 'https://zapier.com', make: 'https://www.make.com',
  n8n: 'https://n8n.io', activepieces: 'https://www.activepieces.com',
  hubspot: 'https://www.hubspot.com', klaviyo: 'https://www.klaviyo.com',
  mailchimp: 'https://mailchimp.com', omnisend: 'https://www.omnisend.com',
  drip: 'https://www.drip.com', activecampaign: 'https://www.activecampaign.com',
  notion: 'https://www.notion.so', notion_ai: 'https://www.notion.so/product/ai',
  jasper: 'https://www.jasper.ai', copy_ai: 'https://www.copy.ai',
  writesonic: 'https://writesonic.com', surfer_seo: 'https://surferseo.com',
  semrush: 'https://www.semrush.com', ahrefs: 'https://ahrefs.com',
  canva: 'https://www.canva.com', midjourney: 'https://www.midjourney.com',
  intercom: 'https://www.intercom.com', zendesk: 'https://www.zendesk.com',
  freshdesk: 'https://www.freshdesk.com', tidio: 'https://www.tidio.com',
  gorgias: 'https://www.gorgias.com', manychat: 'https://manychat.com',
  postscript: 'https://postscript.io', attentive: 'https://www.attentive.com',
  yotpo: 'https://www.yotpo.com', okendo: 'https://www.okendo.io',
  rebuy: 'https://www.rebuyengine.com', nosto: 'https://www.nosto.com',
  google_analytics: 'https://analytics.google.com',
  triple_whale: 'https://www.triplewhale.com', northbeam: 'https://www.northbeam.io',
  polar_analytics: 'https://polaranalytics.com',
  quickbooks: 'https://quickbooks.intuit.com', xero: 'https://www.xero.com',
  freshbooks: 'https://www.freshbooks.com', gusto: 'https://gusto.com',
  rippling: 'https://www.rippling.com', slack: 'https://slack.com',
  monday: 'https://monday.com', asana: 'https://asana.com',
  trello: 'https://trello.com', clickup: 'https://clickup.com',
  airtable: 'https://www.airtable.com', salesforce: 'https://www.salesforce.com',
  pipedrive: 'https://www.pipedrive.com', zoho_crm: 'https://www.zoho.com/crm',
  lightspeed_retail_ai: 'https://www.lightspeedhq.com',
  square: 'https://squareup.com', toast: 'https://pos.toasttab.com',
  woocommerce: 'https://woocommerce.com', bigcommerce: 'https://www.bigcommerce.com',
  typeform: 'https://www.typeform.com', tally: 'https://tally.so',
  loom: 'https://www.loom.com', descript: 'https://www.descript.com',
  calendly: 'https://calendly.com', acuity: 'https://acuityscheduling.com',
  stripe: 'https://stripe.com', chargebee: 'https://www.chargebee.com',
  magicform_ai: 'https://www.magicform.ai', neurons_ai: 'https://www.neuronslab.com',
  lightspeed_scanner: 'https://www.lightspeedhq.com', prisync: 'https://prisync.com',
  brandwatch: 'https://www.brandwatch.com', listen_labs: 'https://www.listenlabs.ai',
  stocktrim: 'https://www.stocktrim.com', limespot: 'https://www.limespot.co',
  aidaptive: 'https://aidaptive.com', visenze: 'https://www.visenze.com',
};

const getToolUrl = (tool) => {
  if (!tool) return null;
  const normalize = (s) => (s || '').toLowerCase().replace(/[\s-]+/g, '_').replace(/[^a-z0-9_]/g, '');
  return (
    tool.affiliate_url ||
    tool.website_url ||
    TOOL_URL_MAP[normalize(tool.id)] ||
    TOOL_URL_MAP[normalize(tool.name)] ||
    tool.raw?.source_url ||
    null
  );
};

// ─── Mission Board helpers ─────────────────────────────────────────────────────
function computeStreak(activityDays) {
  const unique = [...new Set(activityDays)].sort().reverse();
  if (!unique.length) return 0;
  const today = new Date().toISOString().slice(0, 10);
  const yesterday = new Date(Date.now() - 864e5).toISOString().slice(0, 10);
  if (unique[0] !== today && unique[0] !== yesterday) return 0;
  let count = 1;
  for (let i = 1; i < unique.length; i++) {
    const diff = Math.round((new Date(unique[i - 1]) - new Date(unique[i])) / 864e5);
    if (diff === 1) count++; else break;
  }
  return count;
}

function addActivityDay(reportId) {
  try {
    const key = `activity_days_${reportId}`;
    const days = JSON.parse(localStorage.getItem(key) || '[]');
    const today = new Date().toISOString().slice(0, 10);
    if (!days.includes(today)) { days.push(today); localStorage.setItem(key, JSON.stringify(days)); }
    return days;
  } catch (_) { return []; }
}

const AI_STEP_KEYWORDS = ['write', 'create', 'draft', 'set up', 'generate', 'build', 'configure', 'define'];

// ─── FeedbackSection ──────────────────────────────────────────────────────────
const FeedbackSection = ({ reportId, businessName, industry, doneCount = 0 }) => {
  const [submitted, setSubmitted] = React.useState(false);
  const [hoursSaved, setHoursSaved] = React.useState('');
  const [revenueLift, setRevenueLift] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);

  if (submitted) {
    return (
      <div className="my-8 bg-blue-900/20 border border-blue-500/30 rounded-2xl p-6 text-center">
        <CheckCircle2 className="w-8 h-8 text-[#3b82f6] mx-auto mb-2" />
        <p className="text-[#3b82f6] font-semibold">Thanks for sharing your results!</p>
        <p className="text-slate-400 text-sm mt-1">Your outcomes help us improve recommendations for {businessName} and businesses like yours.</p>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await fetch(`${API_BASE}/api/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          report_id: reportId, implemented: true,
          hours_saved_per_week: hoursSaved ? parseFloat(hoursSaved) : null,
          revenue_increase_percent: revenueLift ? parseFloat(revenueLift) : null,
          industry,
        }),
      });
      setSubmitted(true);
    } catch (err) {
      toast.error('Could not save feedback — try again later.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="my-8 bg-slate-900/70 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
      <h3 className="text-lg font-semibold text-white mb-1">
        {doneCount > 0
          ? 'Share your results'
          : "Once you've started implementing, come back and tell us how it's going."}
      </h3>
      <p className="text-sm text-slate-400 mb-4">You've started implementing — tell us how it's going. Takes 30 seconds.</p>
      <form onSubmit={handleSubmit} className="flex flex-wrap gap-4 items-end">
        <div>
          <label className="block text-xs text-slate-400 mb-1">Hours saved per week</label>
          <input type="number" min="0" max="168" step="0.5" value={hoursSaved} onChange={e => setHoursSaved(e.target.value)} placeholder="e.g. 5"
            className="w-32 bg-slate-950/50 border border-white/15 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-[#3b82f6]" />
        </div>
        <div>
          <label className="block text-xs text-slate-400 mb-1">Revenue lift (%)</label>
          <input type="number" min="0" max="1000" step="1" value={revenueLift} onChange={e => setRevenueLift(e.target.value)} placeholder="e.g. 15"
            className="w-32 bg-slate-950/50 border border-white/15 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-[#3b82f6]" />
        </div>
        <button type="submit" disabled={submitting || (!hoursSaved && !revenueLift)}
          className="bg-gradient-to-br from-[#3b82f6] to-blue-500 disabled:opacity-40 text-slate-950 text-sm font-bold px-5 py-2 rounded-full transition-all">
          {submitting ? 'Saving\u2026' : 'Submit results'}
        </button>
      </form>
    </div>
  );
};

const ReferralBlock = ({ reportId, industry, subVertical, t }) => {
  const [copied, setCopied] = React.useState(false);
  const shareUrl = `${window.location.origin}/audit?ref=${reportId}`;
  const handleCopy = () => {
    navigator.clipboard.writeText(shareUrl).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2500); });
  };
  return (
    <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
      className="mb-16 rounded-2xl border border-blue-500/25 bg-gradient-to-br from-blue-950/40 to-slate-900/60 p-8 text-center">
      <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
        <Share2 className="w-6 h-6 text-[#3b82f6]" />
      </div>
      <h2 className="text-2xl font-bold text-white mb-2">
        {(t('report.referral.heading') || '').replace(
          '{industry}',
          formatIndustryLabel(industry, 'SMB', subVertical)
        )}
      </h2>
      <p className="text-slate-400 text-sm max-w-xl mx-auto mb-6">{t('report.referral.subheading')}</p>
      <button onClick={handleCopy}
        className="inline-flex items-center gap-2 bg-gradient-to-br from-[#3b82f6] to-blue-500 text-slate-950 font-bold px-6 py-3 rounded-full transition-all hover:scale-105">
        {copied ? <Check className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}
        {copied ? t('report.referral.copied') : t('report.referral.cta')}
      </button>
      <p className="text-xs text-slate-600 mt-4">{t('report.referral.note')}</p>
    </motion.section>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION SUB-COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════════

function SituationSection({ businessName, industry, subVertical, aiReadinessScore, percentileAhead, tools, phases, totalAnnualValue, year1Net, primaryProblems, onScrollToPlan, graphFingerprint, focusToolName, totalToolCount, problemCount }) {
  const score = aiReadinessScore ?? 50;
  const circumference = 2 * Math.PI * 40;
  const offset = circumference * (1 - score / 100);
  const phaseLabel = score >= 70 ? 'AI-Ready' : score >= 40 ? 'Building' : 'Early Stage';
  // R31.7: defensive coercion — primaryProblems[0] can be a raw object
  // when matched_problems entries lack a `.problem` key (e.g., the
  // matcher returns a node dict with buyer_chip_text but no problem
  // field). React renders objects as "[object Object]". Mirror the
  // GuideTab + DiagnosisTab pattern so the hero stat never displays
  // a stringified object.
  const _topProblemRaw = primaryProblems[0];
  const topProblem =
    typeof _topProblemRaw === 'string'
      ? _topProblemRaw
      : (_topProblemRaw && typeof _topProblemRaw === 'object'
          ? (_topProblemRaw.problem || _topProblemRaw.buyer_chip_text || _topProblemRaw.problem_text || '')
          : '');
  const topProblemShort = topProblem ? (topProblem.length > 95 ? topProblem.slice(0, 92).trim() + '…' : topProblem) : '';
  const monthlyLoss = Math.round(graphFingerprint?.median_monthly_loss || 0);
  const annualK = Math.round((totalAnnualValue || 0) / 1000);
  // catalogSize = the FULL tool catalog size (proper consultant phrasing for
  // "we analyzed N tools" — not the matched-count which dramatically undersells).
  const catalogSize = totalToolCount || 238;
  const matched = tools.length;
  // QA caught the inflated-count bug TWICE: round 2 was the "|| 6" hardcoded
  // fallback; round 4 was the "|| matched" fallback (would surface tools.length
  // ≈ 6-10 as "fixable problems" when the buyer selected 3 chips and the chip
  // count wasn't propagated). Now: ONLY render a count when problemCount is
  // explicitly > 0 from the caller. If the caller can't determine a real
  // count, fixable stays 0 and the conditional renders below suppress the
  // line + stat tile entirely.
  const fixable = problemCount > 0 ? problemCount : 0;
  const startThisWeek = phases?.[0]?.tools?.length || 3;
  const focus = focusToolName || tools[0]?.tool_name || tools[0]?.name || 'your top match';

  return (
    <section className="relative px-6 lg:px-10 pt-10 pb-8 max-w-5xl mx-auto">
      {/* Top eyebrow row: industry tag + AI Readiness score badge */}
      <div className="flex items-center justify-between mb-8">
        <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-blue-400 bg-blue-500/10 border border-blue-500/20 px-3 py-1.5 rounded-full">
          {formatIndustryLabel(industry, 'Business', subVertical)} · AI Blueprint
        </span>
        {aiReadinessScore != null && (
          <div className="flex items-center gap-2.5">
            <div className="relative" style={{ width: 44, height: 44 }}>
              <svg viewBox="0 0 96 96" className="w-full h-full" style={{ transform: 'rotate(-90deg)' }}>
                <circle cx="48" cy="48" r="40" fill="none" stroke="white" strokeOpacity="0.08" strokeWidth="6" />
                <circle cx="48" cy="48" r="40" fill="none" stroke="#3b82f6" strokeWidth="6" strokeLinecap="round"
                  strokeDasharray={circumference}
                  strokeDashoffset={offset}
                  style={{ transition: 'stroke-dashoffset 1.5s ease' }} />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-sm font-black text-white leading-none">{score}</span>
              </div>
            </div>
            <div className="hidden sm:block">
              <p className="text-xs font-bold text-white leading-none">{phaseLabel}</p>
              {percentileAhead > 20 && (
                <p className="text-[10px] text-blue-400 mt-0.5">
                  Ahead of {percentileAhead}% of {formatIndustryLabel(industry, 'peers', subVertical)} · plan targets 70+ in 90 days
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* "Prepared for" eyebrow + business name — document register, not dashboard */}
      <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-2">Prepared for</p>
      <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.0] tracking-tight mb-8" style={{ fontFamily: "'Space_Grotesk', system-ui, sans-serif" }}>
        {businessName || 'Your Business'}
      </h1>

      {/* Consultant-tone synthesis paragraph. Replaces the prior 3-line stack
          (problem mirror + cost line + "we analyzed N tools" line) — same data,
          one paragraph that sounds like analysis instead of a dashboard.
          Catalog size (not matched count) is what justifies "we analyzed". */}
      <p className="text-lg md:text-xl text-slate-300 leading-relaxed max-w-3xl mb-10">
        After analyzing <span className="text-white font-semibold">{catalogSize.toLocaleString()} AI tools</span>, we matched
        {' '}<span className="text-white font-semibold">{matched}</span> to {businessName ? possessive(businessName) : 'your'} situation.
        {topProblemShort && (
          <>
            {' '}Your highest-cost issue — <span className="text-white font-semibold">{topProblemShort}</span>
            {monthlyLoss > 0 && <> (industry typically <span className="text-white font-semibold">~${monthlyLoss.toLocaleString()}/mo</span> at your size)</>}
            {' '}— is addressable with <span className="text-blue-400 font-semibold">{focus}</span> starting this week.
          </>
        )}
        {annualK > 0 && fixable > 0 && (
          <> Across <span className="text-white font-semibold">{fixable} fixable problem{fixable !== 1 ? 's' : ''}</span>, this blueprint targets <span className="text-white font-semibold">${annualK}K per year</span> in recovered margin.</>
        )}
      </p>

      {/* HERO STAT BLOCK — visually distinct from anything else on the page.
          Replaces the prior 4-equal-tile dashboard grid with one dominant
          number + 3 supporting sub-stats. The gradient border + accent line
          gives the page sectional rhythm so the eye knows where to land. */}
      {annualK > 0 && (
        <div className="mb-8 rounded-2xl overflow-hidden relative"
             style={{
               background: 'linear-gradient(135deg, rgba(59,130,246,0.06), rgba(15,23,42,0.5))',
               border: '1px solid rgba(59,130,246,0.25)',
             }}>
          <div className="absolute top-0 left-0 right-0 h-px"
               style={{ background: 'linear-gradient(90deg, transparent, rgba(59,130,246,0.6), transparent)' }} />
          <div className="p-6 md:p-8">
            <div className="flex items-baseline justify-between mb-4 gap-3">
              <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-blue-400">Annual Recovery Target</p>
              <p className="text-[10px] uppercase tracking-[0.15em] text-slate-500">12-week blueprint</p>
            </div>
            <div className="flex items-baseline gap-3 mb-2">
              <span className="text-5xl md:text-7xl font-black text-white tracking-tighter leading-none" style={{ fontFamily: "'Space_Grotesk', system-ui, sans-serif" }}>
                ${annualK}K
              </span>
              <span className="text-base text-slate-400 mb-1.5">/year recoverable</span>
            </div>
            {/* Round-21.1 (Sarah R21 polish): pre-frame the ROI tab's Year 1
                net number so the buyer doesn't whiplash from "$29K hero" →
                "$16,680 Year 1 net" and feel the rug pull. The hero is gross
                recoverable; Year 1 net subtracts setup + tool subscriptions.
                Both are honest, both belong on the same screen. */}
            {year1Net > 0 && year1Net < (totalAnnualValue || 0) && (
              <p className="text-xs text-slate-500 mb-6">
                ≈ <span className="text-slate-400 font-semibold">${Math.round(year1Net / 1000)}K Year 1 net</span> after setup & tool subscriptions · see ROI tab for the full 3-year curve
              </p>
            )}
            <div className="grid grid-cols-3 gap-6 pt-6 border-t border-white/8">
              <div>
                <p className="text-xl md:text-2xl font-bold text-white">{matched}</p>
                <p className="text-[10px] text-slate-500 mt-0.5 uppercase tracking-wider">Tools matched</p>
              </div>
              <div>
                <p className="text-xl md:text-2xl font-bold text-white">{fixable > 0 ? fixable : '—'}</p>
                <p className="text-[10px] text-slate-500 mt-0.5 uppercase tracking-wider">Problems fixable</p>
              </div>
              <div>
                <p className="text-xl md:text-2xl font-bold text-white">{startThisWeek}</p>
                <p className="text-[10px] text-slate-500 mt-0.5 uppercase tracking-wider">Start this week</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CTA */}
      <button onClick={onScrollToPlan}
        className="inline-flex items-center gap-2 bg-gradient-to-br from-[#3b82f6] to-blue-600 text-white font-bold px-8 py-3.5 rounded-full text-sm shadow-lg shadow-blue-500/20 hover:scale-[1.03] active:scale-[0.97] transition-transform">
        Start your implementation plan <ChevronDown className="w-4 h-4" />
      </button>
    </section>
  );
}

// ─── BuildingScreen ────────────────────────────────────────────────────────────
// Replaces the old static 3-step screen that sat unchanged for 60+ seconds.
// Live elapsed-time counter + step checklist that advances on a realistic
// schedule so the buyer always sees motion while LLM enrichment runs.
function BuildingScreen({ industry, subVertical, businessName, problemCount, reportId }) {
  // Round-21.1: live tool count for the loader copy. Falls back to 348 when
  // /api/tools/stats is unreachable so loader never blanks.
  const [liveToolCount, setLiveToolCount] = useState(348);
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL || 'https://ai-workshop-llc-backend.onrender.com'}/api/tools/stats`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d?.total) setLiveToolCount(d.total); })
      .catch(() => {});
  }, []);
  const [elapsed, setElapsed] = React.useState(0);
  React.useEffect(() => {
    // Persist generation start time across refreshes so the elapsed counter
    // doesn't reset to 0 if the buyer reloads the page during the 60-180s
    // LLM enrichment window. QA caught: refresh during generation made the
    // loader look like it was starting over from scratch — buyer reads that
    // as "I just paid $149 and the system is broken" and bails / refunds.
    const key = `building_started_${reportId || 'unknown'}`;
    let t0;
    try {
      const stored = localStorage.getItem(key);
      const parsed = stored ? parseInt(stored, 10) : NaN;
      if (Number.isFinite(parsed) && parsed > 0 && parsed <= Date.now()) {
        t0 = parsed;
      } else {
        t0 = Date.now();
        localStorage.setItem(key, String(t0));
      }
    } catch {
      t0 = Date.now();
    }
    setElapsed(Math.round((Date.now() - t0) / 1000));
    const id = setInterval(() => setElapsed(Math.round((Date.now() - t0) / 1000)), 1000);
    return () => clearInterval(id);
  }, [reportId]);

  // 6 stages; each has an approximate elapsed-seconds threshold when it
  // transitions from "pending" to "active" to "done". Calibrated to match the
  // real LLM gather (exec summary + roadmap + prompts + compliance etc.).
  const industryHuman = formatIndustryLabel(industry, 'your industry', subVertical);
  const stages = [
    { label: `Analyzing ${industryHuman} benchmarks`,                      active: 0,  done: 10 },
    { label: `Scoring ${liveToolCount} tools against your problems`,        active: 10, done: 22 },
    { label: 'Writing your executive summary',                              active: 22, done: 35 },
    { label: 'Generating 22 personalized prompts',                          active: 35, done: 55 },
    { label: 'Drafting your 12-week roadmap + compliance guide',            active: 55, done: 75 },
    { label: 'Calculating ROI projections + finalizing',                    active: 75, done: 95 },
  ];

  return (
    <div className="min-h-screen bg-[#0f172a] flex items-center justify-center px-6">
      <div className="text-center max-w-lg w-full">
        <div className="relative w-24 h-24 mx-auto mb-8">
          <div className="absolute inset-0 rounded-full border-4 border-[#3b82f6]/20" />
          <motion.div animate={{ rotate: 360 }} transition={{ duration: 1.4, repeat: Infinity, ease: 'linear' }}
            className="absolute inset-0 rounded-full border-4 border-transparent border-t-[#3b82f6]" />
          <div className="absolute inset-0 flex items-center justify-center">
            <Sparkles className="w-8 h-8 text-[#3b82f6]" />
          </div>
        </div>
        <motion.h1 initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          className="text-3xl md:text-4xl font-bold text-white mb-3">
          Building{businessName ? ` ${possessive(businessName)}` : ' Your'} Blueprint…
        </motion.h1>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
          className="text-slate-400 text-lg mb-2">
          {industry && <span className="text-[#3b82f6] font-semibold">{industryHuman}</span>}
          {problemCount > 0 && <span className="text-slate-400"> · {problemCount} problem{problemCount !== 1 ? 's' : ''} identified</span>}
        </motion.p>
        <p className="text-xs text-slate-500 mb-6 tabular-nums">
          {elapsed}s elapsed · typically 60–180s on first generation
          {elapsed >= 90 && <span className="text-slate-400"> · still working — LLM is busy today</span>}
        </p>
        <div className="space-y-2.5 text-left bg-slate-900/70 backdrop-blur-xl rounded-2xl p-5 border border-white/10">
          {stages.map((s, i) => {
            const isDone = elapsed >= s.done;
            const isActive = !isDone && elapsed >= s.active;
            return (
              <div key={i} className="flex items-center gap-3">
                <div className={`w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center transition-all duration-300 ${
                  isDone ? 'bg-[#3b82f6]/20 border border-[#3b82f6]'
                    : isActive ? 'bg-[#3b82f6]/10 border border-[#3b82f6]/40'
                    : 'bg-slate-800/50 border border-white/5'
                }`}>
                  {isDone ? (
                    <Check className="w-3 h-3 text-[#3b82f6]" strokeWidth={3} />
                  ) : isActive ? (
                    <motion.div animate={{ scale: [1, 1.4, 1] }} transition={{ duration: 1.2, repeat: Infinity }}
                      className="w-1.5 h-1.5 rounded-full bg-[#3b82f6]" />
                  ) : (
                    <div className="w-1.5 h-1.5 rounded-full bg-slate-600" />
                  )}
                </div>
                <span className={`text-sm transition-colors ${
                  isDone ? 'text-slate-400 line-through decoration-slate-600 decoration-1'
                    : isActive ? 'text-white font-medium'
                    : 'text-slate-500'
                }`}>
                  {s.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── CoachPanel (floating) ────────────────────────────────────────────────────
function CoachPanel({ reportId, industry, businessName, tools, blueprints, askMessages, askInput, setAskInput, askLoading, askQuestionsUsed, ASK_LIMIT, onSubmit }) {
  const [showPanel, setShowPanel] = useState(false);
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem(`report_${reportId}_coach_open`) === 'true'; } catch { return false; }
  });

  useEffect(() => {
    const handler = () => setShowPanel(window.scrollY > window.innerHeight * 0.05);
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  const toggleOpen = () => {
    setOpen(prev => {
      const next = !prev;
      try { localStorage.setItem(`report_${reportId}_coach_open`, String(next)); } catch (_) {}
      return next;
    });
  };

  const pills = [
    `What should I do in my first hour with ${tools[0]?.name ?? 'my first tool'}?`,
    `Write me an email to my team introducing ${tools[0]?.name ?? 'AI tools'} at ${businessName || 'our company'}`,
    `What's the biggest mistake ${industry || 'small business'} owners make when starting with AI?`,
    `I only have 30 minutes today — what's the single most impactful thing I can do?`,
  ];

  const handlePill = (q) => {
    setAskInput(q);
    onSubmit(null, q);
  };

  return (
    <div id="coach-section" className={`fixed bottom-20 right-6 z-50 flex flex-col items-end gap-3 print:hidden transition-all duration-500 ${showPanel ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'}`}>
      {open && (
        <div className="w-96 bg-[#0f1729]/98 backdrop-blur-xl border border-white/12 rounded-2xl shadow-2xl overflow-hidden flex flex-col"
          style={{ maxHeight: '500px' }}>
          <div className="flex items-center gap-3 px-4 py-3 border-b border-white/8 flex-shrink-0">
            <div className="w-8 h-8 rounded-xl bg-blue-500/20 border border-blue-500/30 flex items-center justify-center">
              <Bot className="w-4 h-4 text-[#3b82f6]" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-white">Ask Your AI Coach</p>
              <p className="text-xs text-slate-500">{askQuestionsUsed}/{ASK_LIMIT} used</p>
            </div>
            <button onClick={toggleOpen} className="text-slate-500 hover:text-white transition-colors">
              <XCircle className="w-5 h-5" />
            </button>
          </div>
          {askMessages.length === 0 && (
            <div className="px-4 pt-3 pb-1 flex flex-wrap gap-1.5">
              {pills.map((q, i) => (
                <button key={i} onClick={() => handlePill(q)}
                  className="text-xs border border-white/10 hover:border-blue-500/30 text-slate-400 hover:text-blue-300 px-2.5 py-1 rounded-full transition-colors">
                  {q}
                </button>
              ))}
            </div>
          )}
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 min-h-0">
            {askMessages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[88%] px-3 py-2 rounded-xl text-xs leading-relaxed ${msg.role === 'user' ? 'bg-blue-600/25 text-slate-200 border border-blue-500/20' : 'bg-slate-800/80 text-slate-300 border border-white/6'}`}>
                  {msg.content}
                </div>
              </div>
            ))}
            {askLoading && (
              <div className="flex justify-start">
                <div className="bg-slate-800/80 border border-white/6 px-3 py-2 rounded-xl">
                  <Loader2 className="w-3.5 h-3.5 text-blue-400 animate-spin" />
                </div>
              </div>
            )}
          </div>
          <form onSubmit={onSubmit} className="flex items-center gap-2 px-3 py-3 border-t border-white/8 flex-shrink-0">
            <input value={askInput} onChange={e => setAskInput(e.target.value)}
              placeholder="Ask anything about your blueprint…"
              disabled={askLoading || askQuestionsUsed >= ASK_LIMIT}
              className="flex-1 bg-slate-800/60 border border-white/10 rounded-full px-3 py-1.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-blue-500/40 disabled:opacity-50" />
            <button type="submit" disabled={!askInput.trim() || askLoading || askQuestionsUsed >= ASK_LIMIT}
              className="w-7 h-7 flex items-center justify-center rounded-full bg-gradient-to-br from-[#3b82f6] to-blue-500 disabled:opacity-40 flex-shrink-0">
              <Send className="w-3.5 h-3.5 text-slate-950" />
            </button>
          </form>
        </div>
      )}
      <button onClick={toggleOpen}
        className={`flex items-center gap-2 px-4 py-3 rounded-full shadow-2xl font-semibold transition-all ${open ? 'bg-slate-800 border border-white/15 text-slate-300' : 'bg-gradient-to-br from-[#3b82f6] to-blue-500 text-slate-950'}`}>
        <Bot className="w-5 h-5" />
        Ask Coach
        {!open && <span className="w-2 h-2 rounded-full bg-white animate-pulse" />}
      </button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
// ─── StickyBottomCTA ─────────────────────────────────────────────────────────
function StickyBottomCTA({ tools, focusTool, onTabSwitch, activeTab, doneCount }) {
  const [showStickyBar, setShowStickyBar] = useState(false);
  const ft = focusTool?.tool;
  const ctaToolUrl = ft ? getToolUrl(ft) : (tools[0] ? getToolUrl(tools[0]) : null);
  const ctaToolName = ft?.name || ft?.tool_name || tools[0]?.name || 'Tool';
  const isResume = focusTool?.mode === 'resume';
  const isComplete = focusTool?.mode === 'complete';

  useEffect(() => {
    const handler = () => {
      const scrollPct = window.scrollY / (document.body.scrollHeight - window.innerHeight);
      setShowStickyBar(window.scrollY > 600 && scrollPct < 0.85);
    };
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  if (!showStickyBar || isComplete || !tools[0]) return null;

  // Context-aware messaging based on active tab
  const contextMessage = (() => {
    switch (activeTab) {
      case 'tools': return { title: 'Found your next tool?', sub: `${doneCount}/${tools.length} tools started` };
      case 'financials': return { title: 'See the ROI potential?', sub: 'Start implementing to capture it' };
      case 'prompts': return { title: 'Copy a prompt, then implement', sub: 'Prompts work best with the right tools running' };
      case 'diagnosis': return { title: 'Problems identified — solutions ready', sub: `${tools.length} tools matched to your challenges` };
      default: return {
        title: isResume ? 'Continue where you left off' : 'Ready to start?',
        sub: isResume ? `${ctaToolName} — in progress` : `Tool 1 of ${tools.length} · Takes ~20 minutes`,
      };
    }
  })();

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 bg-gray-950/95 backdrop-blur-xl border-t border-white/10 px-4 py-3 flex items-center justify-between gap-4 print:hidden">
      <div className="hidden md:block">
        <p className="text-white text-sm font-semibold">{contextMessage.title}</p>
        <p className="text-slate-400 text-xs">{contextMessage.sub}</p>
      </div>
      <div className="flex items-center gap-3 ml-auto">
        {ctaToolUrl && (
          <a href={ctaToolUrl} target="_blank" rel="noopener noreferrer"
            className="bg-gradient-to-br from-[#3b82f6] to-blue-600 text-white font-bold px-6 py-2.5 rounded-full text-sm hover:scale-[1.03] active:scale-[0.97] transition-transform">
            {isResume ? `Continue ${ctaToolName.split(' ')[0]}` : `Open ${ctaToolName.split(' ')[0]}`} →
          </a>
        )}
        {activeTab !== 'guide' && (
          <button
            onClick={() => onTabSwitch?.('guide')}
            className="border border-white/20 text-slate-300 font-semibold px-5 py-2.5 rounded-full text-sm hover:border-white/40 transition-colors">
            View Guide
          </button>
        )}
      </div>
    </div>
  );
}

const FullReportPage = () => {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // ── State ────────────────────────────────────────────────────────────────────
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [progress, setProgress] = useState({});
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('guide');
  const [moreTabsOpen, setMoreTabsOpen] = useState(false);
  const [copiedPrompt, setCopiedPrompt] = useState(null);
  const [copiedTool, setCopiedTool] = useState(null);
  const [llmGenerating, setLlmGenerating] = useState(false);
  const [showBuildingScreen, setShowBuildingScreen] = useState(false);
  const [buildingComplete, setBuildingComplete] = useState(false);
  const [totalToolCount, setTotalToolCount] = useState(null);
  const displayToolCount = totalToolCount || 200;
  // [REMOVED] onboardingGoal, onboardingStartWhen — dead code
  const [shareToken, setShareToken] = useState(null);
  const [sharingLink, setSharingLink] = useState(false);
  const [isReturningUser, setIsReturningUser] = useState(false);
  const [daysSinceFirstView, setDaysSinceFirstView] = useState(0);
  const [showReveal, setShowReveal] = useState(false);
  const [revealStep, setRevealStep] = useState(0);
  const [skippedTools, setSkippedTools] = useState([]);
  const [blueprintExpanded, setBlueprintExpanded] = useState(true);
  // [REMOVED] chatOpen, first48Expanded — dead code

  // Mission Board state
  const [stepProgress, setStepProgress] = useState({});
  const [streak, setStreak] = useState(0);
  const [completedPhaseIndices, setCompletedPhaseIndices] = useState([]);
  const [phaseCompleteModal, setPhaseCompleteModal] = useState(null);
  const [toolFlashId, setToolFlashId] = useState(null);

  // Ask AI chat state
  const [askMessages, setAskMessages] = useState([]);
  const [askInput, setAskInput] = useState('');
  const [askLoading, setAskLoading] = useState(false);
  const [askQuestionsUsed, setAskQuestionsUsed] = useState(0);
  const ASK_LIMIT = 20;
  const [networkStats, setNetworkStats] = useState(null);
  const [winBanner, setWinBanner] = useState(null);
  const [pricing, setPricing] = useState({ price: 149, earlyBird: false, spotsRemaining: 0 });
  const [showCoachTooltip, setShowCoachTooltip] = useState(() => {
    try { return !localStorage.getItem(`coach_greeted_${reportId}`); } catch { return true; }
  });

  const { lang } = useLanguage();
  const t = (key, vars) => tWithVars(lang, key, vars);

  const debug = useMemo(() => {
    const q = new URLSearchParams(location.search);
    return (q.get('debug') === 'true' && process.env.NODE_ENV === 'development') || process.env.REACT_APP_DEBUG_REPORT === 'true';
  }, [location.search]);

  const justUnlocked = useMemo(() => {
    return new URLSearchParams(location.search).get('just_unlocked') === 'true';
  }, [location.search]);

  const urlShareToken = useMemo(() => {
    return new URLSearchParams(location.search).get('share_token') || null;
  }, [location.search]);

  // ── Fetch ───────────────────────────────────────────────────────────────────
  useEffect(() => {
    const controller = new AbortController();
    let mounted = true;
    setLoading(true);
    setError(null);

    const fetchReport = async () => {
      try {
        const qs = urlShareToken ? `?share_token=${encodeURIComponent(urlShareToken)}` : '';
        const res = await fetch(`${API_BASE}/api/audit/report/${reportId}${qs}`, { signal: controller.signal });
        if (!res.ok) {
          let errText = `Failed to load report (${res.status})`;
          try { const j = await res.json(); if (j?.message) errText = j.message; } catch (_) {}
          throw new Error(errText);
        }
        const data = await res.json();
        if (!mounted) return;
        if (!data.isPaid) {
          toast.error('Please complete payment to unlock your AI Implementation Blueprint.');
          navigate(`/report-preview/${reportId}`);
          return;
        }
        if (data.shareToken) setShareToken(data.shareToken);
        setReportData(data);
        // R31.3 (Sarah R32 verdict, P0-A): surface chip-resolution drops.
        // When the buyer tapped chips that didn't map to problem nodes,
        // the backend stamps engineMeta.unresolved_chip_ids — show a
        // single soft toast so they know the engine scored against
        // fewer pains than they selected. Soft because the report is
        // still useful for the resolved chips; the buyer's choice is
        // to retry the audit or accept the partial result.
        const _meta = data?.fullReport?.engineMeta;
        if (_meta?.unresolved_chip_ids?.length > 0) {
          const _n = _meta.unresolved_chip_ids.length;
          toast.warning(
            `${_n} chip${_n === 1 ? '' : 's'} couldn't register — your blueprint analyzed ${_meta.resolved_chip_count} of ${_meta.submitted_chip_count} pains. Re-run the audit to capture the rest.`,
            { duration: 8000 },
          );
        }
        // R31.4 (Sarah R33 verdict, P1): email-delivery awareness. Backend
        // stamps `emailDelivery` on the report when Resend rejects (quota
        // exhausted, transient 429, etc.). Replace the unconditional
        // "check your email" success toast with a status-aware variant
        // that tells the buyer the truth: "your report is right here on
        // screen — we'll retry the email." Sarah caught the gaslight in
        // R32 + R33: every paid buyer this hour got the green checkmark
        // and never received an actual email.
        const _delivery = data?.emailDelivery;
        if (_delivery?.status === 'pending_retry') {
          toast.info(
            "Your report is ready right here — we're retrying the email separately. No action needed.",
            { duration: 8000 },
          );
        } else if (_delivery?.status === 'failed') {
          toast.warning(
            "We couldn't deliver the email copy of your report. Use the share link below or the Download PDF button — your report is safe here.",
            { duration: 10000 },
          );
        }
        const isGenerating = data.llmGenerating || false;
        setLlmGenerating(isGenerating);
        if (isGenerating) {
          // LLM is still building — keep the building screen up until it finishes.
          // The poll loop (below) will dismiss it when llmGenerating flips to false.
          setShowBuildingScreen(true);
        } else if (justUnlocked) {
          // Payment just completed AND LLM already finished (fast) — brief reveal animation
          setShowBuildingScreen(true);
          setTimeout(() => {
            setShowBuildingScreen(false);
            setBuildingComplete(true);
            try { localStorage.removeItem(`building_started_${reportId}`); } catch {}
            const revealKey = `reveal_seen_${reportId}`;
            if (!localStorage.getItem(revealKey)) {
              setShowReveal(true);
              setRevealStep(0);
            }
          }, 3000);
        }
        try {
          const saved = localStorage.getItem(`report_progress_${reportId}`);
          const local = saved ? JSON.parse(saved) : {};
          if (data.isPaid) {
            fetch(`${API_BASE}/api/report/${reportId}/progress`)
              .then(r => r.ok ? r.json() : null)
              .then(remote => {
                const hasRemote = remote && (Object.keys(remote.tools || {}).length > 0 || Object.keys(remote.playbooks || {}).length > 0);
                setProgress(hasRemote ? { tools: remote.tools || {}, playbooks: remote.playbooks || {} } : local);
              })
              .catch(() => { setProgress(local); });
          } else { setProgress(local); }
        } catch (err) {
          console.warn('Failed to parse saved progress.', err);
          localStorage.removeItem(`report_progress_${reportId}`);
        }
      } catch (err) {
        if (err.name === 'AbortError') return;
        console.error('fetchReport error:', err);
        setError(err.message || 'Could not load your AI Implementation Blueprint.');
        toast.error(err.message || 'Could not load your AI Implementation Blueprint.');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchReport();
    return () => { mounted = false; controller.abort(); };
  }, [reportId, navigate]);

  // Fetch live tool count
  useEffect(() => {
    fetch(`${API_BASE}/api/tools/stats`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.total) setTotalToolCount(d.total); })
      .catch(() => {});
  }, []);

  // Auto-poll while LLM generating — check every 8s so the buyer isn't
  // staring at the building screen for 30s after the LLM finishes.
  // Cache-busting: append a timestamp param so the browser HTTP cache
  // can't return a stale {llmGenerating: true} response after the LLM
  // has actually finished. Round-7 QA caught a 161s+ stuck loader where
  // the backend was confirming isPaid:true / llmGenerating:false but the
  // frontend kept seeing the old "still generating" body.
  useEffect(() => {
    if (!llmGenerating || !reportId) return;
    const transitionToReport = (data) => {
      setReportData(data);
      setLlmGenerating(false);
      setShowBuildingScreen(false);
      setBuildingComplete(true);
      try { localStorage.removeItem(`building_started_${reportId}`); } catch {}
      toast.success('Your personalized blueprint is ready!');
      const revealKey = `reveal_seen_${reportId}`;
      if (!localStorage.getItem(revealKey)) {
        setShowReveal(true);
        setRevealStep(0);
      }
    };
    const interval = setInterval(async () => {
      try {
        const cacheBust = Date.now();
        const res = await fetch(
          `${API_BASE}/api/audit/report/${reportId}?track_view=false&_=${cacheBust}`,
          { cache: 'no-store' },
        );
        if (!res.ok) return;
        const data = await res.json();
        // Round-26 (Sarah R25 logs): respect server fail signals. When the
        // generation has crashed 3 times, the backend sets llmFailPermanent
        // and exposes lastError. Surface a graceful error UI instead of
        // spinning forever. Sarah's R25 walk had 977s of infinite loader
        // because the frontend ignored the server's failure state.
        if (data.llmFailPermanent || data.lastError) {
          setLlmGenerating(false);
          setShowBuildingScreen(false);
          toast.error(
            'We hit an issue generating your report. Email aiworkshopllc@gmail.com with your report ID and we\'ll send your blueprint manually within 1 hour.',
            { duration: 30000 },
          );
          clearInterval(interval);
          return;
        }
        // R44 (Maya verdict): payload-truth escape hatch — if a complete
        // fullReport exists and the buyer is paid, transition regardless
        // of the llmGenerating flag. Belt-and-suspenders with the
        // watchdog at L914.
        const _hasReadyReport = !!(
          data.isPaid &&
          data.fullReport &&
          (Array.isArray(data.fullReport.aiToolsRecommendations)
            ? data.fullReport.aiToolsRecommendations.length > 0
            : false)
        );
        if (_hasReadyReport || (!data.llmGenerating && data.isPaid)) {
          transitionToReport(data);
          clearInterval(interval);
        }
      } catch (_) {}
    }, 8000);
    return () => clearInterval(interval);
  }, [llmGenerating, reportId]);

  // Watchdog: aggressive backstop for the loader. Round 8 attempted
  // cache-bust + own-fetch but kept reading elapsed time from
  // localStorage, which can race with BuildingScreen's mount-time
  // localStorage write — leading to elapsed=0 forever and the watchdog
  // never firing. Round 8b verdict: "still broken at 131s+". This
  // version owns its own start time (local closure, no race), starts
  // checking at 30s elapsed (not 90s), and force-transitions the moment
  // the backend confirms generation done.
  useEffect(() => {
    if (!showBuildingScreen || !reportId) return;
    let cancelled = false;
    const watchdogStart = Date.now();

    let warnedSlow = false;
    const checkBackend = async () => {
      if (cancelled) return;
      try {
        const cacheBust = Date.now();
        const res = await fetch(
          `${API_BASE}/api/audit/report/${reportId}?track_view=false&_=${cacheBust}`,
          { cache: 'no-store' },
        );
        if (cancelled || !res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        // Round-26: respect server failure signals (llmFailPermanent set
        // after 3 prior crashes; lastError populated on any crash).
        if (data.llmFailPermanent || data.lastError) {
          setLlmGenerating(false);
          setShowBuildingScreen(false);
          toast.error(
            'We hit an issue generating your report. Email aiworkshopllc@gmail.com with your report ID and we\'ll send your blueprint manually within 1 hour.',
            { duration: 30000 },
          );
          return;
        }
        // R44 (Maya verdict): backend's llmGenerating flag can race against
        // the fullReport write — Maya saw 166s+ loader with the complete
        // fullReport object already present in the API response. Trust the
        // payload truth: if isPaid AND fullReport.aiToolsRecommendations
        // exists, the report IS ready, regardless of what the flag says.
        // This is the refund-prevention guarantee — every second past 30s
        // with a complete report is a paying customer staring at a spinner.
        const _hasReadyReport = !!(
          data.isPaid &&
          data.fullReport &&
          (Array.isArray(data.fullReport.aiToolsRecommendations)
            ? data.fullReport.aiToolsRecommendations.length > 0
            : false)
        );
        if (_hasReadyReport || (data.isPaid && !data.llmGenerating)) {
          // Backend confirms ready — force-transition. Don't trust the
          // poll loop's React state (browser HTTP cache caught us in R7).
          setReportData(data);
          setLlmGenerating(false);
          setShowBuildingScreen(false);
          setBuildingComplete(true);
          try { localStorage.removeItem(`building_started_${reportId}`); } catch {}
        }
      } catch (_) {}
    };

    const interval = setInterval(() => {
      if (cancelled) return;
      const elapsed = (Date.now() - watchdogStart) / 1000;
      // Aggressive: start checking at 30s elapsed, every 5s thereafter.
      // 30s is the floor where a real buyer's patience starts to cliff;
      // 5s polling means we beat them to the close button by ~85s if
      // generation completed in the meantime.
      if (elapsed >= 30) checkBackend();
      // Round-26 (Sarah R25): when no failure flag has come back BUT the
      // generation has been running >180s, warn the buyer once with a
      // gentler message before they assume the site is broken. Sarah's
      // R25 walk hit 977s of silence; that's a refund every time.
      // Distinguished from the hard error toast (which fires on
      // llmFailPermanent/lastError) so we don't mislead when the LLM
      // is just legitimately slow.
      if (elapsed >= 180 && !warnedSlow) {
        warnedSlow = true;
        toast(
          'Generation is taking longer than usual. Hang tight — we\'ll keep checking. If the loader stays past 5 minutes, email aiworkshopllc@gmail.com with your report ID.',
          { duration: 20000 },
        );
      }
    }, 5000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [showBuildingScreen, reportId]);

  // Persist progress
  useEffect(() => {
    const toolsMap = progress.tools || {};
    const playbooks = progress.playbooks || {};
    if (!reportId || (Object.keys(toolsMap).length === 0 && Object.keys(playbooks).length === 0 && Object.keys(stepProgress).length === 0)) return;
    const timer = setTimeout(() => {
      try {
        localStorage.setItem(`report_progress_${reportId}`, JSON.stringify(progress));
        localStorage.setItem(`step_progress_${reportId}`, JSON.stringify(stepProgress));
      } catch (err) { console.warn('Failed to save progress', err); }
      if (reportData?.isPaid) {
        const rawTools = reportData?.fullReport?.aiToolsRecommendations || [];
        const allToolIds = rawTools.map(tool => tool.id || tool.tool_name || tool.name).filter(Boolean);
        const fullTools = { ...Object.fromEntries(allToolIds.map(id => [id, 'not_started'])), ...toolsMap };
        fetch(`${API_BASE}/api/report/${reportId}/progress`, {
          method: 'PATCH', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tools: fullTools, playbooks, steps: stepProgress }),
        }).catch(() => {});
      }
    }, 1500);
    return () => clearTimeout(timer);
  }, [progress, stepProgress, reportId, reportData]);

  // Close More dropdown on outside click
  useEffect(() => {
    if (!moreTabsOpen) return;
    const handler = () => setMoreTabsOpen(false);
    document.addEventListener('click', handler, { capture: true });
    return () => document.removeEventListener('click', handler, { capture: true });
  }, [moreTabsOpen]);

  // Returning user detection
  useEffect(() => {
    if (!reportId || !reportData) return;
    const key = `first_viewed_${reportId}`;
    const stored = localStorage.getItem(key);
    if (stored && !justUnlocked) {
      const days = Math.floor((Date.now() - parseInt(stored)) / (1000 * 60 * 60 * 24));
      setDaysSinceFirstView(days);
      setIsReturningUser(true);
    } else if (!stored) {
      localStorage.setItem(key, Date.now().toString());
    }
  }, [reportId, reportData, justUnlocked]);

  // Load skipped tools
  useEffect(() => {
    if (!reportId) return;
    try {
      const r = localStorage.getItem(`skipped_tools_${reportId}`);
      if (r) setSkippedTools(JSON.parse(r));
    } catch (_) {}
  }, [reportId]);

  // Load step progress
  useEffect(() => {
    if (!reportId) return;
    const loadLocal = () => {
      try {
        const s = localStorage.getItem(`step_progress_${reportId}`);
        if (s) setStepProgress(JSON.parse(s));
      } catch (_) {}
    };
    if (reportData?.isPaid) {
      fetch(`${API_BASE}/api/report/${reportId}/progress`)
        .then(r => r.ok ? r.json() : null)
        .then(d => { if (d?.steps && Object.keys(d.steps).length) setStepProgress(d.steps); else loadLocal(); })
        .catch(loadLocal);
    } else { loadLocal(); }
  }, [reportId, reportData?.isPaid]);

  // Compute streak
  useEffect(() => {
    if (!reportId) return;
    try {
      const days = JSON.parse(localStorage.getItem(`activity_days_${reportId}`) || '[]');
      setStreak(computeStreak(days));
    } catch (_) {}
  }, [reportId]);

  // Coach tooltip auto-dismiss
  useEffect(() => {
    if (!showCoachTooltip) return;
    const timer = setTimeout(() => {
      setShowCoachTooltip(false);
      try { localStorage.setItem(`coach_greeted_${reportId}`, '1'); } catch {}
    }, 5000);
    return () => clearTimeout(timer);
  }, [showCoachTooltip, reportId]);

  // ── Destructure reportData ───────────────────────────────────────────────────
  const {
    fullReport, businessName, industry, isPaid, diagnostics: serverDiagnostics,
    aiAssistant, businessType, ecommercePlatform, trajectory, teamLiteracy, dataReadiness,
    subVertical,
  } = reportData || {};

  const assistantLabel =
    aiAssistant === 'grok' ? 'Grok' : aiAssistant === 'chatgpt' ? 'ChatGPT' :
    aiAssistant === 'claude' ? 'Claude' : aiAssistant === 'gemini' ? 'Gemini' : 'your AI assistant';

  const trajectoryLabel = { growth: 'Scaling', optimize: 'Optimizing', pivot: 'Pivoting', launch: 'Launching' }[trajectory] || null;

  // Benchmark network stats
  useEffect(() => {
    if (!industry) return;
    fetch(`${API_BASE}/api/benchmarks/industry/${encodeURIComponent(industry)}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (!d || !d.sample_size) return;
        setNetworkStats({
          sufficient_data: d.sample_size >= 3,
          cohort_size: d.sample_size,
          avg_readiness_score: d.avg_ai_readiness_score,
          data_foundation_required_pct: d.data_foundation_required_pct,
          top_tools_recommended: d.top_tools_recommended ?? [],
        });
      })
      .catch(() => {});
  }, [industry]);

  // Pricing
  useEffect(() => {
    fetch(`${API_BASE}/api/pricing/current`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d && typeof d.price === 'number') setPricing(d); })
      .catch(() => {});
  }, []);

  // ── Normalize ────────────────────────────────────────────────────────────────
  const normalized = useMemo(() => {
    const fr = fullReport || {};
    // R31.7c (Sarah R34d verdict, P1): prefer fullReport.roadmapPhases
    // over fullReport.implementationRoadmap.phases when both exist. The
    // engine writes the new field with PM-tailored / vertical-specific
    // phase titles (e.g. "Phase 1: Tenant Screening Acceleration for
    // Cedar Ridge"); the legacy implementationRoadmap.phases falls back
    // to a generic agent-broker template ("Automate Lead Follow-Up /
    // Streamline Listings & Marketing / Close Deals Faster"). Both
    // coexist for backward compat with older reports; the new field
    // wins when present.
    const roadmap = fr.implementationRoadmap || {};
    const _roadmapPhasesNew = Array.isArray(fr.roadmapPhases) ? fr.roadmapPhases : null;
    const _roadmapPhasesLegacy = Array.isArray(roadmap.phases) ? roadmap.phases : [];
    const phasesRaw = (_roadmapPhasesNew && _roadmapPhasesNew.length > 0)
      ? _roadmapPhasesNew
      : _roadmapPhasesLegacy;
    const toolsRaw = Array.isArray(fr.aiToolsRecommendations) ? fr.aiToolsRecommendations : [];
    const risksRaw = Array.isArray(fr.riskFlags) ? fr.riskFlags : [];
    const actionStepsRaw = Array.isArray(fr.actionSteps) ? fr.actionSteps : [];
    const toolsMap = toolsRaw.map((tool) => ({
      id: tool.id ?? tool.blueprintId ?? sanitize(tool.name ?? tool.tool_name) ?? `tool-${Math.random().toString(36).slice(2, 8)}`,
      name: tool.name ?? tool.tool_name ?? tool.initiativeLabel ?? 'Tool',
      category: tool.category ?? 'General',
      pricing: tool.pricing ?? tool.estimatedMonthlyCost ?? tool.cost ?? 'Varies',
      pricingDetails: tool.pricing_details ?? null,
      hoursSaved: tool.hours_saved ?? tool.hoursSaved ?? null,
      why: tool.why_selected ?? tool.why ?? tool.matchReason ?? tool.description ?? 'Recommended for your needs',
      setupSteps: Array.isArray(tool.setup_steps) ? tool.setup_steps : Array.isArray(tool.setupSteps) ? tool.setupSteps : [],
      successStory: tool.success_story ?? tool.real_world_example ?? null,
      backedBy: (() => {
        const b = tool.backed_by ?? tool.backedBy ?? null;
        if (!b) return null;
        return { sample_size: b.sample_size ?? b.similarBusinesses ?? null, median_lift_percent: b.median_lift_percent ?? b.medianLift ?? null };
      })(),
      integrationComplexity: tool.integration_complexity ?? null,
      performanceMetrics: tool.performance_metrics ?? null,
      match_score: tool.match_score ?? null,
      failure_common_reasons: tool.failure_common_reasons ?? tool.failureCommonReasons ?? null,
      affiliate_url: tool.affiliate_url ?? null,
      alternatives: Array.isArray(tool.alternatives) ? tool.alternatives : [],
      source: tool.source ?? 'AI Workshop internal',
      raw: tool,
    }));

    const toolsById = toolsMap.reduce((acc, tool) => { acc[tool.id] = tool; return acc; }, {});

    const phases = phasesRaw.map((p, idx) => {
      const pId = p.id ?? `phase-${idx + 1}`;
      const pTools = Array.isArray(p.tools) ? p.tools.map((tool) => {
        if (!tool) return null;
        if (typeof tool === 'string') return toolsById[tool] ?? { id: tool, name: tool };
        if (tool.id && toolsById[tool.id]) return toolsById[tool.id];
        const byName = toolsMap.find(tt => tt.name === tool.name || tt.initiativeLabel === tool.initiativeLabel);
        return byName ?? {
          id: tool.id ?? sanitize(tool.name ?? tool.tool_name) ?? `tool-${Math.random().toString(36).slice(2, 8)}`,
          name: tool.name ?? tool.tool_name ?? tool.initiativeLabel ?? 'Tool',
          pricing: tool.estimatedMonthlyCost ?? 'Varies',
          setupSteps: Array.isArray(tool.setup_steps) ? tool.setup_steps : [],
          why: tool.why_selected ?? tool.description ?? 'Recommended for your needs',
        };
      }).filter(Boolean) : [];
      return {
        id: pId, phase: p.phase ?? idx + 1, name: p.name ?? `Phase ${idx + 1}`,
        weeks: p.weeks ?? p.duration_weeks ?? 4,
        estimated_monthly_cost: p.estimated_monthly_cost ?? p.estimatedMonthlyCost ?? null,
        focus: p.focus ?? p.title ?? '', expected_impact: p.expected_impact ?? p.summary ?? '',
        confidence_level: p.confidence_level ?? p.confidence ?? 'Medium',
        weeklyTasks: Array.isArray(p.weeklyTasks) ? p.weeklyTasks : [],
        keyActions: Array.isArray(p.keyActions) ? p.keyActions : [],
        tools: pTools, raw: p,
      };
    });

    const risks = risksRaw.map((r, idx) => ({
      id: r.id ?? `risk-${idx}`, risk: r.risk ?? r.title ?? 'Risk',
      severity: r.severity ?? r.level ?? 'MEDIUM',
      mitigation: Array.isArray(r.mitigation) ? r.mitigation : [], raw: r,
    }));

    const actionSteps = actionStepsRaw.map((s, idx) => ({
      id: s.id ?? `step-${idx}`, action: s.action ?? s.title ?? 'Action',
      description: s.description ?? s.note ?? '', tool: s.tool ?? null,
    }));

    const roi = fr.roiProjections ?? { projectedLiftBand: 'Moderate', approximateImpactIndex: 0.25 };

    const playbooksRaw = Array.isArray(fr.playbooks) ? fr.playbooks : [];
    const playbooks = playbooksRaw.map((pb, idx) => ({
      id: pb.id ?? `playbook-${idx}`, name: pb.name ?? 'AI Playbook', outcome: pb.outcome ?? '',
      problemSummary: Array.isArray(pb.problemSummary) ? pb.problemSummary : [],
      tools: Array.isArray(pb.tools) ? pb.tools : [],
      steps: Array.isArray(pb.steps) ? pb.steps : [],
    }));

    return { toolsMap, tools: toolsMap, phases, risks, actionSteps, roi, playbooks };
  }, [fullReport]);

  const { tools, phases, risks, actionSteps, roi, playbooks } = normalized;

  // Financial data — single source of truth for every dollar number on the
  // page (hero "$X /year opportunity", ROI tab Y1/Y2/Y3, Total 3-Year Net,
  // alternatives row). Earlier the hero read a different aggregate
  // (sum of per-blueprint savingsPerYear) than the ROI tab, producing a
  // dental report that showed "$7k /year" on the hero and "$18,000/year"
  // on the ROI tab. Buyers always pattern-match to the smaller number.
  //
  // Model:
  //   recurringAnnual  = backend's monthly_savings + monthly_revenue_uplift × 12
  //   oneTimeRecovery  = ~25% of cost_of_inaction_12m (bounded ≤ recurringAnnual)
  //                      — represents recoverable backlog that AI can claw
  //                      back in deployment year only.
  //   Y1 returns = recurringAnnual × 0.85 (ramp-in: deployment + learning) + oneTimeRecovery
  //   Y2 returns = recurringAnnual × 1.0  (full steady-state)
  //   Y3 returns = recurringAnnual × 1.10 (10% compounding adoption gains)
  //
  // This produces an INCREASING shape Y1 → Y2 → Y3 (correct for AI tool
  // deployments) instead of the previous Y1 high → Y2 dip → Y3 partial
  // recovery curve, which mistakenly bundled one-time recovery into the
  // baseline that Y2 then "lost".
  //
  // Investment shape:
  //   Y1 = full y1_investment_estimate (tools + setup + training + onboarding)
  //   Y2/Y3 = ~30% of Y1 (recurring subs + light optimization, no setup)
  const financialData = useMemo(() => {
    // Backend-sourced steady-state economics (preferred).
    const monthlySavings   = Number(roi?.estimated_monthly_savings)         || 0;
    const monthlyRevenue   = Number(roi?.estimated_monthly_revenue_uplift)  || 0;
    const recurringAnnual  = Math.round((monthlySavings + monthlyRevenue) * 12);
    const costOfInaction12 = Number(roi?.cost_of_inaction_12m)              || 0;
    const backendInvest    = Number(roi?.y1_investment_estimate)            || 0;

    // Tool-cost fallback when backend roi is empty (legacy reports).
    const totalToolCost = phases.reduce(
      (sum, p) => sum + (p.estimated_monthly_cost ? p.estimated_monthly_cost * (p.weeks / 4) : 0),
      0,
    );

    // Investment: prefer backend, then phase-summed tool cost, then $12K floor.
    const y1Investment = backendInvest > 500
      ? Math.round(backendInvest)
      : (totalToolCost > 500 ? Math.round(totalToolCost) : 12000);
    // Y2/Y3 investment = recurring tool-subscription bill (no setup overhead),
    // so the ramp doesn't produce 1500%+ Y2 ROI from a fake-tiny investment.
    // The earlier "30% of Y1" assumption made sense when Y1 was just tool
    // cost; with Y1 now including setup overhead it produced absurd Y2 ROI.
    // Prefer backend's recurring_stack_annual; fall back to ~70% of Y1 (a
    // reasonable proxy for "Y1 minus setup") then to legacy 30% as last resort.
    const recurringStack = Number(roi?.recurring_stack_annual) || 0;
    const y2Investment = recurringStack > 500
      ? Math.round(recurringStack)
      : (y1Investment >= 6000 ? Math.round(y1Investment * 0.7) : Math.round(y1Investment * 0.3));
    const y3Investment = y2Investment;

    let y1Returns, y2Returns, y3Returns, oneTimeRecovery;
    let usingBackend = recurringAnnual > 0;

    if (usingBackend) {
      // One-time backlog recovery: bounded slice of cost-of-inaction.
      // Capped at recurringAnnual so it never dominates the picture.
      oneTimeRecovery = Math.round(Math.min(costOfInaction12 * 0.25, recurringAnnual));
      y1Returns = Math.round(recurringAnnual * 0.85 + oneTimeRecovery);
      y2Returns = Math.round(recurringAnnual * 1.00);
      y3Returns = Math.round(recurringAnnual * 1.10);
    } else {
      // Legacy fallback — preserve the old impactIdx-based shape but FLATTEN
      // the Y2/Y3 multipliers so the curve no longer dips.
      const impactIdx = roi?.approximateImpactIndex ?? 0.25;
      oneTimeRecovery = 0;
      y1Returns = Math.round(y1Investment * (1 + impactIdx * 6));
      y2Returns = Math.round(y1Investment * (1 + impactIdx * 6) * 1.0);
      y3Returns = Math.round(y1Investment * (1 + impactIdx * 6) * 1.10);
    }

    const y1Net = y1Returns - y1Investment;
    const y2Net = y2Returns - y2Investment;
    const y3Net = y3Returns - y3Investment;
    const pct = (net, inv) => `${Math.round((net / inv) * 100)}%`;
    return {
      years: [
        { year: 1, investment: y1Investment, returns: y1Returns, net: y1Net, roi: pct(y1Net, y1Investment) },
        { year: 2, investment: y2Investment, returns: y2Returns, net: y2Net, roi: pct(y2Net, y2Investment) },
        { year: 3, investment: y3Investment, returns: y3Returns, net: y3Net, roi: pct(y3Net, y3Investment) },
      ],
      // Surfaced separately so the ROI tab can footnote: "Year 1 includes
      // ~$X one-time recovery from backlog; Years 2-3 are steady-state."
      recurringAnnual,
      oneTimeRecovery,
      sensitivity: [
        { name: 'Worst Case', effectiveness: '50%', roi: pct(y1Returns * 0.5 - y1Investment, y1Investment) },
        { name: 'Conservative', effectiveness: '75%', roi: pct(y1Returns * 0.75 - y1Investment, y1Investment) },
        { name: 'Expected', effectiveness: '90%', roi: pct(y1Returns * 0.9 - y1Investment, y1Investment) },
        { name: 'Best Case', effectiveness: '100%', roi: pct(y1Net, y1Investment) },
      ],
      isIllustrative: !usingBackend,
      // R31.7 (Sarah-adjacent finding): the previous alternatives list
      // hardcoded sales/retail/manufacturing comparisons (Physical
      // Expansion, Equipment Upgrade) that read wrong on professional-
      // services and property-management personas. Branched by industry
      // so each buyer sees the comparisons their peers would actually
      // weigh against AI investment. Falls back to the original list
      // when industry isn't a recognized vertical.
      alternatives: ((_industryNorm) => {
        const _PM = ['real_estate', 'real-estate', 'realestate'];
        const _PRO_SVCS = ['professional_services', 'professional-services', 'legal', 'accounting', 'finance', 'consulting'];
        const _SERVICES = ['hvac', 'home_services', 'home-services', 'plumbing', 'auto_repair', 'auto-repair', 'construction', 'med_spa', 'medspa'];
        const _CLINICAL = ['dental', 'healthcare', 'physical_therapy', 'physical-therapy', 'mental_health', 'mental-health', 'veterinary'];
        const _STOREFRONT = ['retail', 'ecommerce', 'e-commerce', 'hospitality', 'restaurant'];
        let alts;
        if (_PM.includes(_industryNorm)) {
          alts = [
            { option: 'Hire a leasing / maintenance coordinator', cost: '$45,000', roi: '120–200%', timeline: '6–12 months' },
            { option: 'Outsource to third-party PM company', cost: '8–10% of rent', roi: '90–180%', timeline: '3–9 months' },
            { option: 'Upgrade your PM platform tier', cost: '$3,600', roi: '60–150%', timeline: '6–12 months' },
            { option: 'Add unit count (acquisition)', cost: '$200,000+', roi: '40–120%', timeline: '24–36 months' },
          ];
        } else if (_PRO_SVCS.includes(_industryNorm)) {
          alts = [
            { option: 'Hire a junior associate', cost: '$75,000', roi: '120–200%', timeline: '9–18 months' },
            { option: 'Outsource paraprofessional work', cost: '$30,000/yr', roi: '80–150%', timeline: '3–6 months' },
            { option: 'Upgrade practice-management software', cost: '$8,000', roi: '50–120%', timeline: '6–12 months' },
            { option: 'Office expansion (new location)', cost: '$80,000+', roi: '30–100%', timeline: '12–24 months' },
          ];
        } else if (_SERVICES.includes(_industryNorm)) {
          alts = [
            { option: 'Hire an additional technician', cost: '$60,000', roi: '120–200%', timeline: '6–12 months' },
            { option: 'Outsource dispatch / call handling', cost: '$25,000/yr', roi: '80–150%', timeline: '2–6 months' },
            { option: 'Add a service truck + tools', cost: '$45,000', roi: '60–150%', timeline: '6–12 months' },
            { option: 'Yellow Pages / Local SEO push', cost: '$15,000', roi: '50–150%', timeline: '3–9 months' },
          ];
        } else if (_CLINICAL.includes(_industryNorm)) {
          alts = [
            { option: 'Hire an additional clinician / hygienist', cost: '$95,000', roi: '120–250%', timeline: '6–12 months' },
            { option: 'Outsource billing / RCM', cost: '6–8% of revenue', roi: '60–140%', timeline: '3–9 months' },
            { option: 'Upgrade EHR / practice software', cost: '$12,000', roi: '50–130%', timeline: '6–12 months' },
            { option: 'Operatory expansion / new chair', cost: '$70,000+', roi: '60–150%', timeline: '12–24 months' },
          ];
        } else if (_STOREFRONT.includes(_industryNorm)) {
          alts = [
            { option: 'Additional Employee', cost: '$50,000', roi: '150–200%', timeline: '6–12 months' },
            { option: 'Traditional / Paid Marketing', cost: '$15,000', roi: '100–300%', timeline: '3–9 months' },
            { option: 'Physical Expansion / new location', cost: '$50,000+', roi: '50–150%', timeline: '12–24 months' },
            { option: 'Equipment / POS Upgrade', cost: '$20,000', roi: '25–75%', timeline: '6–18 months' },
          ];
        } else {
          alts = [
            { option: 'Additional Employee', cost: '$50,000', roi: '150–200%', timeline: '6–12 months' },
            { option: 'Traditional Marketing', cost: '$15,000', roi: '100–300%', timeline: '3–9 months' },
            { option: 'Operations Expansion', cost: '$40,000+', roi: '50–150%', timeline: '12–24 months' },
            { option: 'Equipment / Software Upgrade', cost: '$20,000', roi: '25–75%', timeline: '6–18 months' },
          ];
        }
        return [
          ...alts,
          { option: 'AI Implementation Blueprint (This Plan)', cost: formatCurrency(y1Investment), roi: pct(y1Net, y1Investment), timeline: '3–6 months' },
        ];
      })((industry || '').toLowerCase().trim()),
    };
  }, [roi, phases, industry]);

  // Industry icon
  const industryIcon = useMemo(() => {
    switch (industry?.toLowerCase()) {
      case 'retail': case 'ecommerce': return <ShoppingBag className="w-5 h-5" />;
      case 'hospitality': return <Utensils className="w-5 h-5" />;
      case 'healthcare': return <Stethoscope className="w-5 h-5" />;
      case 'finance': return <Landmark className="w-5 h-5" />;
      case 'manufacturing': return <Factory className="w-5 h-5" />;
      case 'real_estate': return <Home className="w-5 h-5" />;
      case 'technology': return <Cpu className="w-5 h-5" />;
      case 'education': return <GraduationCap className="w-5 h-5" />;
      default: return <Briefcase className="w-5 h-5" />;
    }
  }, [industry]);

  const businessDescriptor = useMemo(() => {
    if (businessType === 'ecommerce' || businessType === 'both') {
      if (ecommercePlatform === 'shopify') return 'your Shopify store';
      if (ecommercePlatform === 'woocommerce') return 'your WooCommerce store';
      if (ecommercePlatform === 'bigcommerce') return 'your ecommerce brand';
      if (ecommercePlatform === 'squarespace') return 'your Squarespace shop';
      if (ecommercePlatform === 'wix') return 'your Wix store';
      return 'your online store';
    }
    if (businessType === 'services') return 'your service business';
    if (industry) return `your ${formatIndustryLabel(industry, 'your industry', subVertical).toLowerCase()} business`;
    return 'your business';
  }, [businessType, ecommercePlatform, industry, subVertical]);

  // Prompt pack
  const promptPack = useMemo(() => {
    if (fullReport?.promptPack && Array.isArray(fullReport.promptPack.prompts) && fullReport.promptPack.prompts.length > 0) {
      return fullReport.promptPack.prompts;
    }
    if (Array.isArray(fullReport?.promptPack) && fullReport.promptPack.length > 0) {
      return fullReport.promptPack;
    }
    const safeName = sanitize(businessName || 'this business');
    const safeIndustry = sanitize(industry || 'this business');
    const matched = (Array.isArray(fullReport?.matchedProblems) && fullReport.matchedProblems.length > 0)
      ? fullReport.matchedProblems : (fullReport?.ownerProblems?.matched_problems ?? []);
    const prompts = matched.slice(0, 3).map((problem, i) => {
      const probText = sanitize(problem?.problem ?? 'general business challenge').slice(0, 200);
      return {
        id: `prompt-${i}`,
        title: `Solve: ${probText.length > 55 ? `${probText.slice(0, 55)}...` : probText}`,
        description: `Tailored to your #${i + 1} challenge`,
        category: 'Operations', impact: 'High', difficulty: 'Beginner', estimatedTime: '5 min',
        bestModel: assistantLabel, supportedModels: [assistantLabel, 'ChatGPT', 'Claude', 'Gemini'],
        roiLabel: 'Saves ~2 hrs/week',
        whyItWorks: 'Provides your specific business context to get actionable advice instead of generic answers.',
        source: 'AI Workshop LLC Prompt Library', chainWith: [],
        prompt: `You are an expert AI consultant helping ${safeName} (${safeIndustry}).\nThe owner's biggest challenge is: "${probText}".\nCreate a practical, step-by-step action plan I can implement this week. Include specific AI tools, estimated time savings, and first actions to take today.`,
      };
    });
    if (prompts.length === 0) {
      return [{
        id: 'fallback-1', title: 'Identify Your Top AI Opportunity', description: 'General business analysis prompt',
        category: 'Strategy', impact: 'High', difficulty: 'Beginner', estimatedTime: '5 min',
        bestModel: assistantLabel, supportedModels: [assistantLabel, 'ChatGPT', 'Claude', 'Gemini'],
        roiLabel: 'Saves ~3 hrs/week',
        whyItWorks: 'Role prompting forces the AI to act as a domain expert rather than a generalist.',
        source: 'AI Workshop LLC Prompt Library', chainWith: [],
        prompt: `You are an expert AI consultant helping ${safeName} (${safeIndustry}).\nList the top 3 highest-ROI AI automations for a business like mine, with specific tool names, estimated hours saved per week, and setup difficulty (Beginner/Intermediate/Advanced).`,
      }];
    }
    return prompts;
  }, [fullReport, businessName, industry, assistantLabel]);

  const thirtyDayWins = useMemo(() => {
    const toolsList = Array.isArray(phases[0]?.tools) ? phases[0].tools : [];
    return toolsList.length ? toolsList.map(tool => tool.name ?? 'Quick AI setup') : ['Start with basic AI audit'];
  }, [phases]);

  const projectedSavings = useMemo(() => Math.round((roi?.approximateImpactIndex ?? 0.25) * (tools.length || 3) * 600), [roi, tools]);
  const projectedRevenue = useMemo(() => Math.round((roi?.approximateImpactIndex ?? 0.25) * (phases.length || 2) * 2500), [roi, phases]);
  const roiBand = roi?.projectedLiftBand ?? 'Moderate';

  const aiReadinessScore = useMemo(() => {
    const s = fullReport?.aiMaturityIndex?.score ?? fullReport?.executiveSummary?.aiReadinessScore ?? fullReport?.aiReadinessScore ?? null;
    if (s == null) return null;
    return s <= 10 ? Math.round(s * 10) : Math.round(s);
  }, [fullReport]);

  // doneCount must be before liveAiScore (dependency)
  const doneCount = useMemo(() => {
    const tp = progress?.tools || {};
    return tools.filter(t => tp[t.id] === 'done').length;
  }, [tools, progress]);

  // liveAiScore must be before scorePhase (dependency — TDZ fix)
  const liveAiScore = useMemo(() => {
    const baseScore = aiReadinessScore ?? 35;
    const toolBonus = doneCount * 3;                     // +3 per completed tool (max ~30)
    const stepBonus = Object.values(stepProgress).flat().filter(Boolean).length * 0.3; // +0.3 per checked step
    const streakBonus = Math.min(streak, 7);             // +1 per streak day (max 7)
    return Math.min(100, Math.round(baseScore + toolBonus + stepBonus + streakBonus));
  }, [aiReadinessScore, doneCount, stepProgress, streak]);

  // Score milestone toasts
  const prevLiveScoreRef = useRef(null);
  useEffect(() => {
    if (prevLiveScoreRef.current === null) { prevLiveScoreRef.current = liveAiScore; return; }
    const prev = prevLiveScoreRef.current;
    prevLiveScoreRef.current = liveAiScore;
    if (prev < 50 && liveAiScore >= 50) toast.success(`Building Phase achieved! You're ahead of most ${formatIndustryLabel(industry, 'similar', subVertical)} businesses.`);
    else if (prev < 70 && liveAiScore >= 70) toast.success(`On Track! Your AI implementation is accelerating.`);
    else if (prev < 90 && liveAiScore >= 90) toast.success(`AI-Ready! Top 10% of ${formatIndustryLabel(industry, '', subVertical)} businesses.`);
  }, [liveAiScore, industry]);

  const scorePhase = useMemo(() => {
    if (liveAiScore == null) return null;
    if (liveAiScore >= 70) return {
      label: 'AI-Ready', color: 'text-blue-400', borderColor: 'border-blue-500/30', bgColor: 'bg-blue-500/10',
      message: `You're ahead of ${networkStats?.avg_readiness_score ? Math.round(((liveAiScore - networkStats.avg_readiness_score) / networkStats.avg_readiness_score) * 100) : ''}% of ${formatIndustryLabel(industry, 'your industry', subVertical)} peers. The window is open — first movers typically capture 3–5× more ROI.`
    };
    if (liveAiScore >= 40) return {
      label: 'Building', color: 'text-amber-400', borderColor: 'border-amber-500/30', bgColor: 'bg-amber-500/10',
      message: `You're in the active adoption phase — the critical window where implementation quality determines who wins. The tools in this blueprint are selected to close your specific gaps.`
    };
    return {
      label: 'Early Stage', color: 'text-slate-400', borderColor: 'border-slate-500/30', bgColor: 'bg-slate-500/10',
      message: `Starting here is an advantage, not a liability — businesses that adopt AI in year 1 spend 40% less on implementation than late movers. Your blueprint starts with the highest-ROI, lowest-complexity tools.`
    };
  }, [liveAiScore, industry, networkStats]);

  const testimonials = useMemo(() => {
    const key = (industry || '').toLowerCase();
    return TESTIMONIAL_BANK[key] ?? TESTIMONIAL_BANK.default;
  }, [industry]);

  const competitiveIntelligence = fullReport?.competitiveIntelligence || {};
  const hasCompetitiveData = Boolean(
    competitiveIntelligence?.competitors?.length > 0 ||
    competitiveIntelligence?.marketTrends?.length > 0 ||
    competitiveIntelligence?.differentiators?.length > 0
  );
  const implementationTimeline = fullReport?.implementationTimeline || fullReport?.implementationGuide || {};
  const matchedProblems = (() => {
    if (Array.isArray(fullReport?.matchedProblems) && fullReport.matchedProblems.length > 0) return fullReport.matchedProblems;
    if (Array.isArray(fullReport?.ownerProblems?.matched_problems)) return fullReport.ownerProblems.matched_problems;
    return [];
  })();

  const topUnstartedTool = useMemo(() => {
    return tools.find(tool => { const status = progress?.tools?.[tool.id]; return !status || status === 'not_started'; }) || tools[0] || null;
  }, [tools, progress]);

  const toolsStartedCount = useMemo(() => {
    return tools.filter(tool => { const s = progress?.tools?.[tool.id]; return s && s !== 'not_started'; }).length;
  }, [tools, progress]);

  const focusTool = useMemo(() => {
    if (!tools.length) return null;
    const tp = progress?.tools || {};
    const inProg = tools.find(t => tp[t.id] === 'in_progress');
    if (inProg) return { tool: inProg, mode: 'resume' };
    const next = tools.find(t => tp[t.id] !== 'done' && !skippedTools.includes(t.id));
    if (next) return { tool: next, mode: 'start' };
    const allDone = tools.every(t => tp[t.id] === 'done');
    if (allDone) return { tool: null, mode: 'complete' };
    return { tool: null, mode: 'all_skipped' };
  }, [tools, progress, skippedTools]);

  const focusToolProblem = useMemo(() => {
    const ft = focusTool?.tool;
    if (!ft || !matchedProblems.length) return null;
    // Round-13 (Sarah R12 verdict): the previous fuzzy keyword overlap
    // (split tool.why into words, search matchedProblems[*].problem for
    // matching words) caused the Plan tab "Addresses This Challenge"
    // card to render whichever catalog problem happened to share the
    // most words with tool.why — even when that catalog problem was
    // padding from the seed corpus, not Sarah's actual chip. Sarah saw
    // "Organic search drives almost no traffic. Category pages have
    // one paragraph..." rendered under Shopify Magic on the Plan tab.
    //
    // Backend already maintains the canonical tool->problem linkage via
    // _attach_recommended_tools_to_problems: every problem has a
    // recommended_tools[] array containing the tool IDs that solve it.
    // Inverting that map gives us tool_id -> problem, no fuzzy match.
    const ftId = ft.id || ft.tool_name || ft.name;
    const direct = matchedProblems.find(p =>
      Array.isArray(p.recommended_tools) && p.recommended_tools.includes(ftId)
    );
    if (direct) return direct;
    // Fallback: tools with a singular `recommended_tool` object (older
    // backend shape). Match its id to the focusTool id.
    const directObj = matchedProblems.find(p =>
      p.recommended_tool && (p.recommended_tool.id === ftId || p.recommended_tool.tool_name === ftId)
    );
    if (directObj) return directObj;
    // No backend linkage: render the first matched problem (which is
    // Sarah's #1 chip post-R11) rather than running fuzzy keyword
    // overlap. First-chip fallback is honest — it's literally what
    // she tapped, even if the tool was matched via a different signal.
    return matchedProblems[0] || null;
  }, [focusTool, matchedProblems]);

  const focusPhaseNumber = useMemo(() => {
    const tp = progress?.tools || {};
    for (let i = 0; i < phases.length; i++) {
      if (!(phases[i].tools || []).every(t => tp[t.id] === 'done')) return i + 1;
    }
    return phases.length || 1;
  }, [phases, progress]);

  const currentStepIndex = useMemo(() => {
    const ft = focusTool?.tool;
    if (!ft) return 0;
    const checks = stepProgress[ft.id] || [];
    const first = checks.findIndex(c => !c);
    return first === -1 ? (ft.setupSteps?.length || 6) : first;
  }, [focusTool, stepProgress]);

  const focusToolPlaybook = useMemo(() => {
    const ft = focusTool?.tool;
    if (!ft || !playbooks.length) return null;
    return playbooks.find(pb => (pb.tools || []).some(t => t.id === ft.id || t.tool_name === ft.name || t.name === ft.name)) || null;
  }, [focusTool, playbooks]);

  const valueUnlocked = useMemo(() => {
    if (!tools.length) return 0;
    const totalMonthly = (roi?.estimated_monthly_savings || 0) + (roi?.estimated_monthly_revenue_uplift || 0);
    if (!totalMonthly) return 0;
    return Math.round((doneCount / tools.length) * totalMonthly);
  }, [doneCount, tools.length, roi]);

  const valueTotal = useMemo(() => {
    return (roi?.estimated_monthly_savings || 0) + (roi?.estimated_monthly_revenue_uplift || 0);
  }, [roi]);

  const connectionChain = useMemo(() => {
    if (!focusToolPlaybook) return [];
    return (focusToolPlaybook.tools || []).map(t => t.name || t.tool_name).filter(Boolean).slice(0, 4);
  }, [focusToolPlaybook]);

  const aiCompletableSteps = useMemo(() => {
    const ft = focusTool?.tool;
    if (!ft?.setupSteps) return {};
    return ft.setupSteps.reduce((acc, step, idx) => {
      const lower = (typeof step === 'string' ? step : (step?.step || step?.action || '')).toLowerCase();
      if (AI_STEP_KEYWORDS.some(kw => lower.includes(kw))) acc[idx] = true;
      return acc;
    }, {});
  }, [focusTool]);

  // Phase completion detection
  useEffect(() => {
    if (!phases.length || !tools.length) return;
    const tp = progress?.tools || {};
    phases.forEach((phase, idx) => {
      if (completedPhaseIndices.includes(idx)) return;
      const phaseTools = phase.tools || [];
      if (!phaseTools.length) return;
      if (phaseTools.every(t => tp[t.id] === 'done')) {
        setCompletedPhaseIndices(prev => [...prev, idx]);
        setPhaseCompleteModal({ phase, index: idx });
      }
    });
  }, [progress?.tools, phases, tools.length, completedPhaseIndices]);

  // Auto-expand blueprint when all tools done
  useEffect(() => {
    if (focusTool?.mode === 'complete') setBlueprintExpanded(true);
  }, [focusTool]);

  // ── Handlers ─────────────────────────────────────────────────────────────────
  const copyToClipboard = useCallback((text, id) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedPrompt(id);
      setTimeout(() => setCopiedPrompt(null), 2000);
    }).catch(() => { toast.error(t('report.toasts.copyError')); });
  }, [t]);

  const handleShare = useCallback(async () => {
    setSharingLink(true);
    try {
      let token = shareToken;
      if (!token) {
        const res = await fetch(`${API_BASE}/api/audit/report/${reportId}/share`, { method: 'POST' });
        if (!res.ok) throw new Error('Could not create share link');
        const data = await res.json();
        token = data.shareToken;
        setShareToken(token);
      }
      const url = `${window.location.origin}/full-report/${reportId}?share_token=${token}`;
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const ta = document.createElement('textarea');
        ta.value = url; ta.setAttribute('readonly', ''); ta.style.position = 'absolute'; ta.style.left = '-9999px';
        document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta);
      }
      toast.success('Share link copied! Anyone with this link can view your blueprint.');
    } catch (err) {
      console.error('handleShare failed', err);
      toast.error('Could not create share link. Please try again.');
    } finally { setSharingLink(false); }
  }, [reportId, shareToken, t]);


  const handleDownloadPDF = useCallback(async () => {
    const params = new URLSearchParams();
    if (lang) params.set('lang', lang);
    const url = `${API_BASE}/api/report/${reportId}/download?${params.toString()}`;
    setDownloading(true);
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`PDF generation failed (${res.status})`);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `AI_Implementation_Plan_${(reportData?.businessName || 'Business').replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
      toast.success(t('report.toasts.downloadSuccess') || 'PDF downloaded!');
    } catch (err) {
      toast.error(`PDF download failed: ${err.message}`);
    } finally {
      setDownloading(false);
    }
  }, [reportId, lang, t, reportData?.businessName]);

  const handleRetry = useCallback(() => window.location.reload(), []);
  const handleUpgrade = useCallback(() => navigate('/upgrade'), [navigate]);

  const handleGoToTool = useCallback((toolId) => {
    setActiveTab('tools');
    setBlueprintExpanded(true);
    setTimeout(() => {
      const el = document.getElementById(`tool-card-${toolId}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 300);
  }, []);

  const handleSkipTool = useCallback((toolId) => {
    setSkippedTools(prev => {
      const next = [...prev, toolId];
      try { localStorage.setItem(`skipped_tools_${reportId}`, JSON.stringify(next)); } catch (_) {}
      return next;
    });
  }, [reportId]);

  const handleProgressUpdate = useCallback((toolId, status) => {
    setProgress(prev => ({ ...prev, tools: { ...(prev.tools || {}), [toolId]: status } }));
  }, []);

  const handleStepCheck = useCallback((toolId, stepIdx) => {
    setStepProgress(prev => {
      const current = [...(prev[toolId] || [])];
      current[stepIdx] = !current[stepIdx];
      const next = { ...prev, [toolId]: current };
      try { localStorage.setItem(`step_progress_${reportId}`, JSON.stringify(next)); } catch (_) {}
      const days = addActivityDay(reportId);
      setStreak(computeStreak(days));
      const tool = tools.find(t => t.id === toolId);
      if (tool && tool.setupSteps?.length > 0 && current.filter(Boolean).length === tool.setupSteps.length) {
        setToolFlashId(toolId);
        setTimeout(() => { setToolFlashId(null); handleProgressUpdate(toolId, 'done'); }, 800);
        setWinBanner({ toolName: tool.name, hoursSaved: tool.hoursSaved ?? null });
        setTimeout(() => setWinBanner(null), 3800);
      } else if (current[stepIdx] && progress.tools?.[toolId] !== 'in_progress') {
        handleProgressUpdate(toolId, 'in_progress');
      }
      return next;
    });
  }, [reportId, tools, progress.tools, handleProgressUpdate]);

  const handleStuck = useCallback((toolId, stepIdx, stepText) => {
    const ft = tools.find(t => t.id === toolId);
    if (!ft) return;
    const problemCtx = focusToolProblem?.problem ? ` I'm trying to solve: "${focusToolProblem.problem}".` : '';
    const prompt = `I'm setting up ${ft.name} for my ${industry} business called ${businessName}.${problemCtx} I'm stuck on step ${stepIdx + 1}: "${stepText}". What exactly should I do?`;
    setActiveTab('ask');
    setBlueprintExpanded(true);
    setAskInput(prompt);
    setTimeout(() => { document.getElementById('ask-input')?.focus(); }, 300);
  }, [tools, industry, businessName, focusToolProblem]);

  const handleDoWithAI = useCallback((toolId, stepIdx, stepText) => {
    const ft = tools.find(t => t.id === toolId);
    if (!ft) return;
    const prompt = `For my ${industry} business called ${businessName}, help me complete this exact step for ${ft.name}: "${stepText}". Give me the specific output I can copy-paste directly into the tool. Be specific to my industry.`;
    setActiveTab('ask');
    setBlueprintExpanded(true);
    setAskInput(prompt);
    setTimeout(() => { document.getElementById('ask-input')?.focus(); }, 300);
  }, [tools, industry, businessName]);

  const handleAskSubmit = useCallback(async (e, overrideQ) => {
    e?.preventDefault();
    const q = overrideQ ?? askInput.trim();
    if (!q || askLoading) return;
    if (askQuestionsUsed >= ASK_LIMIT) {
      toast.error(`You've used all ${ASK_LIMIT} questions for this hour. Try again later.`);
      return;
    }
    setAskInput('');
    setAskMessages(prev => [...prev, { role: 'user', content: q }]);
    setAskLoading(true);

    // Try streaming first, fall back to non-streaming
    try {
      const res = await fetch(`${API_BASE}/api/report/${reportId}/ask/stream`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question: q }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Error ${res.status}`);
      }

      // SSE streaming via ReadableStream
      if (res.body && typeof res.body.getReader === 'function') {
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let fullAnswer = '';

        // Add empty assistant message to fill in progressively
        setAskMessages(prev => [...prev, { role: 'assistant', content: '' }]);

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            try {
              const evt = JSON.parse(line.slice(6));
              if (evt.token) {
                fullAnswer += evt.token;
                const snapshot = fullAnswer;
                setAskMessages(prev => {
                  const updated = [...prev];
                  updated[updated.length - 1] = { role: 'assistant', content: snapshot };
                  return updated;
                });
              }
              if (evt.error) throw new Error(evt.error);
            } catch (_) {}
          }
        }
        setAskQuestionsUsed(prev => prev + 1);
      } else {
        // Fallback: read full response as JSON (no ReadableStream support)
        const data = await res.json();
        setAskMessages(prev => [...prev, { role: 'assistant', content: data.answer || '' }]);
        setAskQuestionsUsed(prev => prev + 1);
      }
    } catch (err) {
      // Fallback to non-streaming endpoint
      try {
        const res2 = await fetch(`${API_BASE}/api/report/${reportId}/ask`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ question: q }),
        });
        if (res2.ok) {
          const data = await res2.json();
          setAskMessages(prev => [...prev, { role: 'assistant', content: data.answer }]);
          setAskQuestionsUsed(prev => prev + 1);
        } else {
          throw err;
        }
      } catch (_) {
        setAskMessages(prev => [...prev, { role: 'assistant', content: `Sorry, I couldn't answer that right now. (${err.message})` }]);
      }
    } finally { setAskLoading(false); }
  }, [askInput, askLoading, askQuestionsUsed, ASK_LIMIT, reportId]);

  // New derived state for cinematic layout
  const blueprints = useMemo(() => {
    const raw = fullReport?.rankedBlueprints ?? fullReport?.playbooks ?? normalized.playbooks;
    if (!Array.isArray(raw) || !raw.length) return [];
    return raw.slice(0, 3).map((b, i) => ({
      id: b.id ?? `bp-${i}`,
      name: b.name ?? b.initiativeLabel ?? 'AI Initiative',
      savingsPerYear: b.cost_savings_per_year_usd ?? b.annual_savings ?? Math.round((financialData.years[0]?.net ?? 15000) / Math.max(raw.length, 1)),
      timeToValueWeeks: b.time_to_value_weeks ?? b.weeks ?? 4,
      difficulty: b.difficulty ?? b.implementation_complexity ?? b.implementationComplexity ?? 3,
      sampleSize: b.sample_size ?? b.backedBy?.sample_size ?? 0,
      successRate: b.observed_success_rate ?? b.backedBy?.observed_success_rate ?? null,
      steps: Array.isArray(b.implementation_steps) ? b.implementation_steps : (Array.isArray(b.steps) ? b.steps : []),
      problemSummary: Array.isArray(b.problemSummary) ? b.problemSummary : [],
      recommendedToolIds: b.recommended_tool_ids ?? [],
      tools: Array.isArray(b.tools) ? b.tools : [],
      outcome: b.outcome ?? '',
    }));
  }, [fullReport, normalized.playbooks, financialData]);

  // Hero "$X /year recoverable" pulls from roi.canonical_annual — the
  // chip-derived single-source-of-truth that the backend stamps once after
  // the CANONICAL_ROI override. Sarah's R20 funnel walk caught $28,800 →
  // $22,500 → $17K → $56,160 on the same persona because each surface
  // re-derived from a different upstream (chip-canonical vs y1Net vs
  // 3-year sum). Pinning the hero to canonical_annual matches the "/year
  // recoverable" label and aligns landing tile + preview tile + hero +
  // ROI tab. Falls back to y1Net only when canonical_annual is missing
  // (legacy reports written before R21).
  const totalOpportunity = useMemo(() => {
    const canonical = Number(roi?.canonical_annual) || Number(roi?.cost_of_inaction_12m) || 0;
    if (canonical > 0) return canonical;
    return Math.max(0, financialData.years[0]?.net || 0);
  }, [roi, financialData]);
  const percentileAhead = useMemo(() => Math.min(95, Math.max(5, Math.round(liveAiScore ?? 50))), [liveAiScore]);
  const primaryProblems = useMemo(() => {
    // Prefer the user's actual chip-clicked LABELS over the backend's matched-
    // problem prose. The matched problems are taxonomy entries with verbose
    // benchmark-flavored text (e.g. "We have a 15-20% no-show rate every
    // single day. That's thousands in lost revenue..."). When a user-echo
    // section says "Your priorities", it must use the user's own words.
    // The matched-problem prose stays available for severity / benchmark
    // commentary, which IS where that prose belongs.
    const chips = fullReport?.userResponses?.problemChipsSelected
               || fullReport?.ownerProblems?.problemChipsSelected
               || [];
    if (Array.isArray(chips) && chips.length) return chips.filter(Boolean);
    const mp = Array.isArray(fullReport?.matchedProblems)
      ? fullReport.matchedProblems
      : (fullReport?.ownerProblems?.matched_problems ?? []);
    return mp.map(p => p.problem ?? p).filter(Boolean);
  }, [fullReport]);

  const opportunitySectionRef = useRef(null);
  const scrollToPlan = useCallback(() => opportunitySectionRef.current?.scrollIntoView({ behavior: 'smooth' }), []);

  // ─── Building Screen ─────────────────────────────────────────────────────────
  // QA caught: the loader said "6 problems identified" when the buyer
  // selected only 3 chips — engine free-text expansion grew matched_problems
  // past the chip count, but the loader wording made it look like the
  // system invented 3 extra issues. Prefer the buyer's chip count when
  // available so "I selected 3, the loader confirms 3" matches their mental
  // model. Fall back to matched_problems only when no chips were sent.
  if (showBuildingScreen) {
    const _chipCount = Array.isArray(fullReport?.userResponses?.problemChipsSelected)
      ? fullReport.userResponses.problemChipsSelected.filter(Boolean).length
      : 0;
    const _displayedCount = _chipCount > 0 ? _chipCount : matchedProblems.length;
    return <BuildingScreen industry={industry} subVertical={subVertical} businessName={businessName} problemCount={_displayedCount} reportId={reportId} />;
  }

  // ─── Story-Mode Reveal (first visit after payment) ──────────────────────────
  if (showReveal && tools.length > 0) {
    const graphFp = fullReport?.graphFingerprint || {};
    const monthlyLoss = graphFp.median_monthly_loss || 0;
    const topTool = tools[0];
    const topProblem = primaryProblems[0] || matchedProblems[0]?.problem || '';
    const score = aiReadinessScore ?? 50;
    const pctAhead = Math.min(95, Math.max(5, Math.round(score)));

    const advanceReveal = () => {
      if (revealStep < 2) {
        setRevealStep(prev => prev + 1);
      } else {
        // Dismiss reveal
        setShowReveal(false);
        localStorage.setItem(`reveal_seen_${reportId}`, 'true');
      }
    };

    return (
      <div className="min-h-screen bg-[#0f172a] flex items-center justify-center px-6 cursor-pointer select-none"
        onClick={advanceReveal}>
        <AnimatePresence mode="wait">
          {/* ── Screen 1: The Mirror — "We heard you" ── */}
          {revealStep === 0 && (
            <motion.div key="reveal-mirror"
              initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95, y: -20 }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
              className="text-center max-w-md w-full">
              <motion.p initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
                className="text-slate-500 text-xs uppercase tracking-widest font-semibold mb-6">
                We analyzed {displayToolCount}+ tools for {businessName || 'your business'}
              </motion.p>

              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
                className="bg-slate-800/60 backdrop-blur-xl rounded-2xl border border-white/10 p-7 mb-6 text-left">
                <p className="text-slate-400 text-xs uppercase tracking-wider font-bold mb-3">Top priority we matched:</p>
                {topProblem && (
                  <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }}
                    className="text-xl font-bold text-white leading-snug mb-4">
                    {typeof topProblem === 'string' ? topProblem : topProblem.problem || topProblem}
                  </motion.p>
                )}
                {monthlyLoss > 0 && (
                  <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1.3 }}
                    className="bg-red-500/8 border border-red-500/15 rounded-xl px-4 py-3">
                    {/* Round-8 reframe: this number is the industry-typical
                        median monthly loss for the buyer's top problem,
                        scaled to their business size — NOT a claim about
                        their specific revenue. Sarah's round-7 verdict
                        caught this surface still saying "Estimated cost of
                        this problem $7,500/month" while the hero recovery
                        target was $14K/yr — same problem, mis-framed. */}
                    <p className="text-red-400 text-xs font-semibold mb-1">Industry-typical loss for this problem at your size</p>
                    <p className="text-red-300 text-2xl sm:text-3xl font-black tabular-nums">
                      ~${Math.round(monthlyLoss).toLocaleString()}<span className="text-red-400/50 text-sm sm:text-base font-medium">/month</span>
                    </p>
                  </motion.div>
                )}
                {!monthlyLoss && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3 }}
                    className="text-slate-400 text-sm">
                    We found <span className="text-white font-bold">{tools.length} AI tools</span> that directly address this
                  </motion.div>
                )}
              </motion.div>

              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.0 }}
                className="text-[#3b82f6] font-semibold text-lg">
                We found a fix →
              </motion.p>

              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.8 }}
                className="text-slate-600 text-xs mt-8 animate-pulse">
                Tap to continue
              </motion.p>
            </motion.div>
          )}

          {/* ── Screen 2: The Fix — specific tool + proof ── */}
          {revealStep === 1 && (
            <motion.div key="reveal-fix"
              initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95, y: -20 }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
              className="text-center max-w-md w-full">
              {topTool && (
                <>
                  <motion.p initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
                    className="text-slate-500 text-xs uppercase tracking-widest font-semibold mb-6">
                    Your #1 recommendation
                  </motion.p>

                  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
                    className="bg-slate-800/60 backdrop-blur-xl rounded-2xl border border-[#3b82f6]/20 p-6 mb-6">
                    <div className="flex items-center justify-center gap-3 mb-5">
                      <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-[#3b82f6]/10 border border-[#3b82f6]/20 flex items-center justify-center flex-shrink-0">
                        <span className="text-sm sm:text-lg font-black text-[#3b82f6]">{topTool.match_score || topTool.matchScore || '90'}%</span>
                      </div>
                      <div className="text-left min-w-0">
                        <p className="text-xl sm:text-2xl font-bold text-white truncate">{topTool.name || topTool.tool_name}</p>
                        <p className="text-[#3b82f6] text-sm font-medium">match for {businessName || 'your business'}</p>
                      </div>
                    </div>

                    {topTool.backedBy && (
                      <div className="grid grid-cols-2 gap-3">
                        {topTool.backedBy.similarBusinesses > 0 && (
                          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8 }}
                            className="bg-slate-900/60 rounded-xl p-3">
                            <p className="text-2xl font-black text-white">{Number(topTool.backedBy.similarBusinesses).toLocaleString()}</p>
                            <p className="text-xs text-slate-400">{formatIndustryLabel(industry, 'Similar', subVertical)} businesses use this</p>
                          </motion.div>
                        )}
                        {(topTool.backedBy.medianLiftPercent || topTool.backedBy.median_lift_percent) > 0 && (
                          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.0 }}
                            className="bg-slate-900/60 rounded-xl p-3">
                            <p className="text-2xl font-black text-[#3b82f6]">
                              {topTool.backedBy.medianLiftPercent || topTool.backedBy.median_lift_percent}%
                            </p>
                            <p className="text-xs text-slate-400">median revenue lift</p>
                          </motion.div>
                        )}
                      </div>
                    )}

                    {topTool.setupSteps?.[0] && (
                      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3 }}
                        className="mt-4 pt-4 border-t border-white/5 text-left">
                        <p className="text-xs text-slate-500 uppercase font-bold mb-1">First step (takes ~20 min)</p>
                        <p className="text-sm text-slate-300">
                          {typeof topTool.setupSteps[0] === 'string' ? topTool.setupSteps[0] : topTool.setupSteps[0]?.step || topTool.setupSteps[0]?.action || ''}
                        </p>
                      </motion.div>
                    )}
                  </motion.div>
                </>
              )}

              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6 }}
                className="text-slate-600 text-xs mt-6 animate-pulse">
                Tap to see your full plan
              </motion.p>
            </motion.div>
          )}

          {/* ── Screen 3: The Full Picture ── */}
          {revealStep === 2 && (
            <motion.div key="reveal-plan"
              initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
              className="text-center max-w-md w-full">
              <motion.p initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
                className="text-slate-500 text-xs uppercase tracking-widest font-semibold mb-6">
                Your complete blueprint
              </motion.p>

              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
                className="grid grid-cols-3 gap-2 sm:gap-3 mb-8">
                <div className="bg-slate-800/60 border border-white/10 rounded-xl p-3 sm:p-4 text-center">
                  <p className="text-2xl sm:text-3xl font-black text-white tabular-nums">{tools.length}</p>
                  <p className="text-[10px] sm:text-xs text-slate-400 mt-1">tools matched</p>
                </div>
                <div className="bg-slate-800/60 border border-white/10 rounded-xl p-3 sm:p-4 text-center">
                  <p className="text-2xl sm:text-3xl font-black text-white tabular-nums">{phases.length || 3}</p>
                  <p className="text-[10px] sm:text-xs text-slate-400 mt-1">phases</p>
                </div>
                <div className="bg-slate-800/60 border border-white/10 rounded-xl p-3 sm:p-4 text-center">
                  <p className="text-2xl sm:text-3xl font-black text-[#3b82f6] tabular-nums">${Math.round((totalOpportunity || 0) / 1000)}k</p>
                  <p className="text-[10px] sm:text-xs text-slate-400 mt-1">annual value</p>
                </div>
              </motion.div>

              {score > 0 && (
                <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }}
                  className="text-sm text-slate-400 mb-8">
                  AI Readiness: <span className="text-white font-bold">{score}/100</span>
                  {pctAhead > 20 && <span className="text-[#3b82f6]"> — ahead of {pctAhead}% of {formatIndustryLabel(industry, 'similar', subVertical)} businesses</span>}
                </motion.p>
              )}

              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.0 }}
                className="flex flex-col gap-3">
                {(() => { const tUrl = getToolUrl(topTool); return tUrl ? (
                  <a href={tUrl} target="_blank" rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="w-full flex items-center justify-center gap-2 bg-gradient-to-br from-[#3b82f6] to-blue-600 text-white font-bold py-4 rounded-full text-base transition-all hover:scale-[1.02] active:scale-[0.98] shadow-lg shadow-blue-500/20">
                    <Rocket className="w-4 h-4" /> Open {(topTool.name || topTool.tool_name || '').split(' ')[0]} now
                  </a>
                ) : null; })()}
                <button onClick={(e) => { e.stopPropagation(); advanceReveal(); }}
                  className="w-full bg-white/5 hover:bg-white/10 border border-white/15 text-white font-semibold py-3.5 rounded-full text-sm transition-all">
                  Explore full blueprint →
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  // ─── Loading ─────────────────────────────────────────────────────────────────
  if (loading) return (
    <div className="min-h-screen bg-[#0f172a] flex flex-col items-center justify-center gap-8 px-6">
      <div className="w-14 h-14 rounded-2xl bg-blue-500/20 flex items-center justify-center animate-pulse">
        <Sparkles className="w-7 h-7 text-blue-400" />
      </div>
      <div className="space-y-4 text-center">
        {[
          'Analyzing your audit answers…',
          'Matching AI tools to your business…',
          'Building your 12-week roadmap…',
        ].map((label, i) => (
          <div key={i} className="flex items-center gap-3 text-sm text-slate-400 justify-center">
            <div
              className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse"
              style={{ animationDelay: `${i * 300}ms` }}
            />
            {label}
          </div>
        ))}
      </div>
    </div>
  );

  // ─── Error ───────────────────────────────────────────────────────────────────
  if (error || !reportData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0f172a] p-6">
        <div className="max-w-md w-full bg-slate-900/70 backdrop-blur-xl border border-white/10 rounded-2xl p-8 text-center">
          <div className="w-14 h-14 bg-red-500/15 border border-red-500/30 rounded-2xl flex items-center justify-center mx-auto mb-5">
            <AlertTriangle className="w-7 h-7 text-red-400" />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Couldn't load your blueprint</h2>
          <p className="text-slate-400 text-sm mb-5 leading-relaxed">
            {error || 'We could not load your AI Implementation Blueprint right now.'}
          </p>
          <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl px-4 py-3 mb-6 text-left">
            <p className="text-[#3b82f6] text-xs font-semibold mb-1">Your purchase is safe</p>
            <p className="text-slate-400 text-xs leading-relaxed">
              Your report is saved. Check your email for the direct link, or retry below. If issues persist, email <span className="text-white">support@aiworkshopllc.com</span> with Report ID: <span className="text-slate-300 font-mono text-[11px]">{reportId}</span>
            </p>
          </div>
          <div className="flex flex-col gap-2">
            <Button onClick={handleRetry} className="bg-gradient-to-br from-[#3b82f6] to-blue-500 text-slate-950 font-bold w-full rounded-full">Try Again</Button>
            <Button variant="outline" className="border-white/15 text-slate-300 w-full rounded-full"
              onClick={() => window.location.href = `mailto:support@aiworkshopllc.com?subject=Blueprint%20Access%20Issue&body=Report%20ID%3A%20${reportId}`}>
              Email Support
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ─── MAIN RENDER ─────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-950 text-white font-sans">
      <Helmet>
        <title>{businessName ? t('report.pageTitleWithName', { businessName }) : t('report.pageTitle')}</title>
        <meta name="description" content={t('report.metaDescription', { businessName: businessName || '', industry: formatIndustryLabel(industry, '', subVertical) || '' })} />
      </Helmet>

      {/* Dot-grid background */}
      <div className="fixed inset-0 opacity-[0.03] pointer-events-none"
        style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '32px 32px' }}
        aria-hidden="true" />

      {/* ── Sticky header ── */}
      <header className="fixed top-0 left-0 right-0 z-50 h-14 bg-gray-950/95 backdrop-blur-xl border-b border-white/5 flex items-center px-6 gap-4 print:hidden">
        <Link to="/" className="flex items-center gap-2 flex-shrink-0">
          <img src="/logo.jpg" alt="AI Workshop LLC" className="h-7 w-auto rounded-lg" />
          <span className="hidden sm:block text-sm font-bold text-white">AI Workshop <span className="text-[#3b82f6]">LLC</span></span>
        </Link>
        <span className="flex-1 text-sm font-semibold truncate text-white/70">
          {businessName ? `${possessive(businessName)} Blueprint` : 'AI Blueprint'}
        </span>
        {/* Progress badge */}
        {doneCount > 0 && (
          <div className="hidden sm:flex items-center gap-2 flex-shrink-0">
            <div className="flex items-center gap-1.5 bg-blue-500/10 border border-blue-500/20 px-2.5 py-1 rounded-full">
              <div className="w-12 h-1.5 bg-slate-800 rounded-full overflow-hidden">
                <div className="h-full bg-[#3b82f6] rounded-full transition-all" style={{ width: `${Math.round((doneCount / Math.max(tools.length, 1)) * 100)}%` }} />
              </div>
              <span className="text-[10px] text-blue-400 font-bold tabular-nums">{doneCount}/{tools.length}</span>
            </div>
          </div>
        )}
        <div className="flex items-center gap-2 flex-shrink-0">
          <button onClick={handleShare} disabled={sharingLink}
            className="hidden md:flex items-center gap-1.5 text-xs text-slate-400 hover:text-white border border-white/10 hover:border-white/25 px-3 py-1.5 rounded-full transition-colors disabled:opacity-50">
            {sharingLink ? <Loader2 className="w-3 h-3 animate-spin" /> : <Share2 className="w-3 h-3" />}
            Share
          </button>
          <button onClick={handleDownloadPDF} disabled={downloading}
            className="flex items-center gap-1.5 text-xs font-semibold bg-gradient-to-br from-[#3b82f6] to-blue-500 text-slate-950 px-3 py-1.5 rounded-full disabled:opacity-60">
            {downloading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
            PDF
          </button>
        </div>
      </header>

      {/* ── Value Framing Bar ── */}
      {/* Mobile: flex-wrap + smaller px gives narrow phones room to breathe;
          desktop: keeps the single-line look. Earlier versions had px-6
          gap-3 with no wrap — on 360px screens the buyer's name pushed
          the right side of the bar past the viewport edge and the page
          itself scrolled horizontally. */}
      <div className="bg-blue-950/40 border-b border-blue-500/15 px-4 sm:px-6 py-2 sm:py-2.5 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-xs sm:text-sm print:hidden">
        <span className="text-blue-400 font-bold flex-shrink-0">✓ Unlocked</span>
        <span className="text-slate-300 text-center break-words">{displayToolCount}+ tools analyzed · <span className="text-white font-semibold">{tools.length} matched</span> to {businessName || 'your business'}</span>
        {doneCount > 0 ? (
          <>
            <span className="hidden md:block text-slate-600">·</span>
            <span className="hidden md:block text-blue-400 text-xs font-semibold">{doneCount}/{tools.length} tools implemented</span>
          </>
        ) : (
          <>
            <span className="hidden md:block text-slate-600">·</span>
            <span className="hidden md:block text-slate-400 text-xs">Roadmap + prompts + implementation guide</span>
          </>
        )}
      </div>

      <main className="pt-14">
        {/* ── 1. Condensed Hero ── */}
        <SituationSection
          businessName={businessName}
          industry={industry}
          subVertical={subVertical}
          aiReadinessScore={liveAiScore}
          percentileAhead={percentileAhead}
          tools={tools}
          phases={phases}
          totalAnnualValue={totalOpportunity}
          year1Net={Math.max(0, financialData.years[0]?.net || 0)}
          primaryProblems={primaryProblems}
          onScrollToPlan={() => {
            setActiveTab('guide');
            setTimeout(() => document.getElementById('guide-tab-content')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
          }}
          graphFingerprint={fullReport?.graphFingerprint}
          focusToolName={focusTool?.tool?.name || focusTool?.tool?.tool_name || null}
          totalToolCount={totalToolCount}
          /* Prefer the buyer's chip count over the engine-expanded matched
             problems count. Round-4 QA caught the hero narrative + stat
             tile both showing "6 fixable problems" when the buyer selected
             only 3 chips — same root cause as the round-2 loader-count
             bug, different code path. */
          problemCount={(() => {
            // Round-17 (Sarah dental verdict): always source from
            // matched_problems.length, NOT chip-submission count.
            // Previous logic preferred chip count, which diverged from
            // the Problems tab whenever a chip failed to resolve to a
            // catalog node — Mark tapped 3 chips, only 2 resolved
            // (case_acceptance had no dental node before R17), so the
            // headline said "3 fixable problems" while the Problems tab
            // rendered 2 cards. matched_problems.length is the actual
            // resolved count and the only honest number to surface.
            // Falls through to chip count only when matched_problems
            // is unexpectedly empty (defensive — shouldn't happen).
            return matchedProblems.length || (
              Array.isArray(fullReport?.userResponses?.problemChipsSelected)
                ? fullReport.userResponses.problemChipsSelected.filter(Boolean).length
                : 0
            );
          })()}
        />

        {/* ── Returning User Nudge ── */}
        {isReturningUser && daysSinceFirstView > 7 && doneCount === 0 && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
            className="max-w-6xl mx-auto px-4 mt-4 print:hidden">
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl px-5 py-3 flex items-center gap-3">
              <Flame className="w-5 h-5 text-amber-400 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm text-amber-200 font-medium">Your blueprint is waiting — start with just 30 minutes today</p>
                <p className="text-xs text-amber-400/60 mt-0.5">It's been {daysSinceFirstView} days since you unlocked this report</p>
              </div>
              <button onClick={() => setActiveTab('guide')}
                className="text-xs font-bold text-amber-400 hover:text-amber-300 whitespace-nowrap transition-colors">
                Get started →
              </button>
            </div>
          </motion.div>
        )}

        {/* ── Returning User Score Delta ── */}
        {isReturningUser && liveAiScore > (aiReadinessScore ?? 35) && (
          <div className="max-w-6xl mx-auto px-4 mt-2 print:hidden">
            <p className="text-xs text-center text-slate-500">
              AI Readiness: <span className="text-[#3b82f6] font-bold">{liveAiScore}/100</span>
              {' '}· <span className="text-[#3b82f6]">+{liveAiScore - (aiReadinessScore ?? 35)} since you started</span>
            </p>
          </div>
        )}

        {/* ── 2. Tabbed Content ── */}
        <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setMoreTabsOpen(false); }} className="max-w-6xl mx-auto px-4 pb-12">
          {/* ── Sticky Tab Bar ── */}
          <div className="sticky top-12 z-30 bg-[#0f172a]/95 backdrop-blur-xl border-b border-white/10 -mx-4 px-4 py-2 print:hidden">
            <div className="flex items-center gap-1">
              <TabsList className="bg-transparent flex items-center gap-1 overflow-x-auto scrollbar-hide h-auto p-0 flex-1">
                {[
                  { value: 'guide', label: 'Your Plan', icon: <Rocket className="w-3.5 h-3.5" /> },
                  { value: 'tools', label: 'Your Stack', icon: <Wrench className="w-3.5 h-3.5" /> },
                  { value: 'diagnosis', label: 'Problems', icon: <Target className="w-3.5 h-3.5" /> },
                  { value: 'financials', label: 'ROI', icon: <DollarSign className="w-3.5 h-3.5" /> },
                  { value: 'prompts', label: 'Prompts', icon: <Sparkles className="w-3.5 h-3.5" /> },
                ].map(tab => (
                  <TabsTrigger key={tab.value} value={tab.value}
                    className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-semibold whitespace-nowrap transition-all
                      text-slate-400 hover:text-white hover:bg-white/5
                      data-[state=active]:text-blue-400 data-[state=active]:bg-blue-500/10 data-[state=active]:border data-[state=active]:border-blue-500/25
                      shadow-none">
                    {tab.icon} {tab.label}
                  </TabsTrigger>
                ))}
              </TabsList>

              {/* ── More Dropdown ── */}
              <div className="relative flex-shrink-0">
                <button onClick={() => setMoreTabsOpen(!moreTabsOpen)}
                  className={`flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-semibold whitespace-nowrap transition-all
                    ${moreTabsOpen ? 'text-blue-400 bg-blue-500/10' : 'text-slate-400 hover:text-white hover:bg-white/5'}`}>
                  More <ChevronDown className={`w-3 h-3 transition-transform ${moreTabsOpen ? 'rotate-180' : ''}`} />
                </button>
                <AnimatePresence>
                  {moreTabsOpen && (
                    <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                      className="absolute right-0 top-full mt-1 bg-slate-900/95 backdrop-blur-xl border border-white/10 rounded-xl shadow-2xl shadow-black/40 py-1 min-w-[180px] z-50">
                      {[
                        { value: 'playbooks', label: 'Playbooks', icon: <BookOpen className="w-3.5 h-3.5" /> },
                        { value: 'market', label: 'Market Intel', icon: <TrendingUp className="w-3.5 h-3.5" /> },
                        ...(normalized?.risks?.length > 0 ? [{ value: 'risks', label: 'Risks', icon: <ShieldAlert className="w-3.5 h-3.5" /> }] : []),
                        ...(fullReport?.complianceGuide ? [{ value: 'compliance', label: 'Compliance', icon: <Lock className="w-3.5 h-3.5" /> }] : []),
                        ...(hasCompetitiveData ? [{ value: 'competitive', label: 'Competitive', icon: <BarChart className="w-3.5 h-3.5" /> }] : []),
                        // Governance, Checklist, Departments removed — compliance warnings merged into Risks
                      ].map(tab => (
                        <button key={tab.value}
                          onClick={() => { setActiveTab(tab.value); setMoreTabsOpen(false); }}
                          className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-xs font-medium transition-colors
                            ${activeTab === tab.value ? 'text-blue-400 bg-blue-500/10' : 'text-slate-300 hover:text-white hover:bg-white/5'}`}>
                          {tab.icon} {tab.label}
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>

          {/* ── Tab Panels ── */}
          <TabsContent value="guide" forceMount id="guide-tab-content" className="data-[state=inactive]:hidden pt-8">
            {/* ── Mission Card: "Your Next 30 Minutes" ── */}
            {focusTool && (
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
                className="mb-8 rounded-2xl border overflow-hidden"
                style={{
                  background: focusTool.mode === 'complete'
                    ? 'linear-gradient(135deg, rgba(59,130,246,0.08), rgba(37,99,235,0.04))'
                    : 'linear-gradient(135deg, rgba(15,23,42,0.8), rgba(15,23,42,0.6))',
                  borderColor: focusTool.mode === 'complete' ? 'rgba(59,130,246,0.3)' : 'rgba(255,255,255,0.1)',
                }}>
                {/* Mission Card Header */}
                <div className="px-5 py-3 border-b border-white/5 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {focusTool.mode === 'start' && (
                      <>
                        <Zap className="w-4 h-4 text-[#3b82f6]" />
                        <span className="text-xs font-bold text-[#3b82f6] uppercase tracking-wider">Your Next 30 Minutes</span>
                      </>
                    )}
                    {focusTool.mode === 'resume' && (
                      <>
                        <RotateCcw className="w-4 h-4 text-amber-400" />
                        <span className="text-xs font-bold text-amber-400 uppercase tracking-wider">Pick Up Where You Left Off</span>
                      </>
                    )}
                    {focusTool.mode === 'complete' && (
                      <>
                        <Trophy className="w-4 h-4 text-[#3b82f6]" />
                        <span className="text-xs font-bold text-[#3b82f6] uppercase tracking-wider">All {tools.length} Tools Launched</span>
                      </>
                    )}
                    {focusTool.mode === 'all_skipped' && (
                      <>
                        <ListTodo className="w-4 h-4 text-slate-400" />
                        <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Review Skipped Tools</span>
                      </>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    {streak > 0 && <span className="text-amber-400">{streak >= 3 ? '🔥' : ''} {streak}-day streak</span>}
                    {focusTool.mode === 'start' && (fullReport?.graphFingerprint?.median_monthly_loss > 0) && (
                      <span className="text-red-400/70">~${Math.round(fullReport.graphFingerprint.median_monthly_loss / 30).toLocaleString()}/day cost of waiting</span>
                    )}
                    {focusTool.mode !== 'complete' && <span>Phase {focusPhaseNumber} of {phases.length || 3}</span>}
                  </div>
                </div>

                {/* Mission Card Body */}
                <div className="p-5">
                  {/* ── Start / Resume mode ── */}
                  {(focusTool.mode === 'start' || focusTool.mode === 'resume') && focusTool.tool && (() => {
                    const ft = focusTool.tool;
                    const steps = ft.setupSteps || [];
                    const checks = stepProgress[ft.id] || [];
                    const completedSteps = checks.filter(Boolean).length;
                    const totalSteps = steps.length || 6;
                    const hoursSaved = ft.hoursSaved || ft.hours_saved_weekly || null;
                    const monthlySavings = hoursSaved ? Math.round(hoursSaved * (roi?.labor_benchmark_hourly || 75) * 4.33) : null;
                    const toolUrl = getToolUrl(ft);

                    return (
                      <div className="flex flex-col md:flex-row gap-5">
                        {/* Left: tool info + problem */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-[#3b82f6]/10 border border-[#3b82f6]/20 flex items-center justify-center flex-shrink-0">
                              <span className="text-[11px] sm:text-sm font-black text-[#3b82f6]">{ft.match_score || ft.matchScore || '90'}%</span>
                            </div>
                            <div className="min-w-0 flex-1">
                              <h3 className="text-base sm:text-lg font-bold text-white truncate">{ft.name || ft.tool_name}</h3>
                              <p className="text-xs text-slate-400">{ft.match_score || ft.matchScore || '90'}% match for {businessName || 'your business'}</p>
                            </div>
                          </div>

                          {focusToolProblem && (
                            <div className="bg-slate-950/50 rounded-xl p-3 mb-3 border border-white/5">
                              <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-1">Addresses this challenge:</p>
                              <p className="text-sm text-slate-300 leading-snug">
                                {typeof focusToolProblem === 'string' ? focusToolProblem : focusToolProblem.problem || ''}
                              </p>
                              <p className="text-xs text-[#3b82f6] mt-1.5 font-medium">Matched to your business profile →</p>
                            </div>
                          )}

                          {/* Before / After contrast */}
                          {hoursSaved && (
                            <div className="grid grid-cols-2 gap-2 mt-2">
                              <div className="bg-red-500/5 border border-red-500/10 rounded-lg px-3 py-2">
                                <p className="text-[10px] text-red-400 uppercase font-bold mb-0.5">Before</p>
                                <p className="text-sm text-red-300 font-semibold">{hoursSaved}h/week manual</p>
                                {monthlySavings && <p className="text-[10px] text-red-400/70">${monthlySavings.toLocaleString()}/mo wasted</p>}
                              </div>
                              <div className="bg-blue-500/5 border border-blue-500/10 rounded-lg px-3 py-2">
                                <p className="text-[10px] text-[#3b82f6] uppercase font-bold mb-0.5">After</p>
                                <p className="text-sm text-blue-300 font-semibold">Automated</p>
                                {monthlySavings && <p className="text-[10px] text-blue-400/70">Saves ${monthlySavings.toLocaleString()}/mo</p>}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Right: current step + actions */}
                        <div className="md:w-64 flex-shrink-0">
                          {/* Progress bar */}
                          <div className="flex items-center gap-2 mb-3">
                            <div className="flex-1 h-1.5 bg-slate-800 rounded-full overflow-hidden">
                              <motion.div className="h-full bg-[#3b82f6] rounded-full"
                                initial={{ width: 0 }}
                                animate={{ width: `${(completedSteps / totalSteps) * 100}%` }}
                                transition={{ duration: 0.5 }} />
                            </div>
                            <span className="text-xs text-slate-500 tabular-nums flex-shrink-0">{completedSteps}/{totalSteps}</span>
                          </div>

                          {/* Current step */}
                          {steps[currentStepIndex] && (() => {
                            const stepText = typeof steps[currentStepIndex] === 'string' ? steps[currentStepIndex] : steps[currentStepIndex]?.step || steps[currentStepIndex]?.action || '';
                            const isAiCompletable = aiCompletableSteps[currentStepIndex];
                            return (
                              <div className="bg-white/5 rounded-xl p-3 mb-3">
                                <div className="flex items-center justify-between mb-1">
                                  <p className="text-[10px] text-slate-500 uppercase font-bold">Step {currentStepIndex + 1}</p>
                                  {isAiCompletable && (
                                    <span className="text-[9px] bg-purple-500/15 text-purple-400 border border-purple-500/20 px-1.5 py-0.5 rounded-full font-semibold">AI can help</span>
                                  )}
                                </div>
                                <p className="text-sm text-white font-medium leading-snug">{stepText}</p>
                                <div className="flex gap-1.5 mt-2">
                                  {isAiCompletable && (
                                    <button onClick={() => handleDoWithAI(ft.id, currentStepIndex, stepText)}
                                      className="flex items-center gap-1 text-[10px] text-purple-400 hover:text-purple-300 bg-purple-500/10 hover:bg-purple-500/15 border border-purple-500/20 px-2 py-1 rounded-lg transition-colors">
                                      <Bot className="w-3 h-3" /> Do with AI
                                    </button>
                                  )}
                                  <button onClick={() => handleStuck(ft.id, currentStepIndex, stepText)}
                                    className="flex items-center gap-1 text-[10px] text-slate-400 hover:text-slate-300 bg-white/5 hover:bg-white/8 border border-white/10 px-2 py-1 rounded-lg transition-colors">
                                    <HelpCircle className="w-3 h-3" /> Stuck?
                                  </button>
                                </div>
                              </div>
                            );
                          })()}

                          {/* Gotcha warning */}
                          {(() => {
                            const failures = ft.failure_common_reasons || ft.failureCommonReasons || [];
                            const reasons = Array.isArray(failures) ? failures : typeof failures === 'string' ? failures.split('\n').filter(Boolean) : [];
                            if (!reasons.length) return null;
                            return (
                              <div className="bg-amber-900/10 border border-amber-500/20 rounded-xl px-3 py-2 mb-3">
                                <p className="text-[10px] text-amber-400 font-bold uppercase mb-1">⚠ Common gotcha</p>
                                <p className="text-xs text-slate-300">{reasons[0]}</p>
                              </div>
                            );
                          })()}

                          {/* Action buttons */}
                          <div className="flex gap-2">
                            {toolUrl && (
                              <a href={toolUrl} target="_blank" rel="noopener noreferrer"
                                className="flex-1 flex items-center justify-center gap-1.5 bg-gradient-to-br from-[#3b82f6] to-blue-600 text-white text-xs font-bold py-2.5 rounded-xl transition-all hover:scale-[1.02] active:scale-[0.98]">
                                <Rocket className="w-3.5 h-3.5" /> Open {(ft.name || ft.tool_name || '').split(' ')[0]}
                              </a>
                            )}
                            <button
                              onClick={() => handleStepCheck(ft.id, currentStepIndex)}
                              className="flex items-center justify-center gap-1.5 bg-white/5 hover:bg-white/10 border border-white/10 text-white text-xs font-semibold py-2.5 px-3 rounded-xl transition-colors">
                              <Check className="w-3.5 h-3.5" /> Done
                            </button>
                            <button
                              onClick={() => handleSkipTool(ft.id)}
                              className="flex items-center justify-center text-slate-500 hover:text-slate-300 text-xs py-2.5 px-2 rounded-xl transition-colors"
                              title="Skip this tool">
                              <ChevronRight className="w-3.5 h-3.5" />
                            </button>
                          </div>

                          {/* Coming up */}
                          {connectionChain.length > 1 && (
                            <p className="text-[10px] text-slate-600 mt-3">
                              Coming up: {connectionChain.filter(n => n !== (ft.name || ft.tool_name)).slice(0, 3).join(' → ')}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  {/* ── Complete mode ── */}
                  {focusTool.mode === 'complete' && (
                    <div className="text-center py-4">
                      <div className="text-4xl mb-3">🎉</div>
                      <h3 className="text-xl font-bold text-white mb-1">All {tools.length} Tools Launched!</h3>
                      <div className="flex items-center justify-center gap-4 sm:gap-6 mt-4 mb-5 flex-wrap">
                        {aiReadinessScore != null && (
                          <div className="text-center">
                            <p className="text-2xl sm:text-3xl font-black text-[#3b82f6] tabular-nums">{aiReadinessScore}<span className="text-slate-500 text-sm sm:text-lg">/100</span></p>
                            <p className="text-[10px] sm:text-xs text-slate-500">AI Readiness</p>
                          </div>
                        )}
                        {valueUnlocked > 0 && (
                          <div className="text-center">
                            <p className="text-2xl sm:text-3xl font-black text-white tabular-nums">${valueUnlocked.toLocaleString()}<span className="text-slate-500 text-sm sm:text-lg">/mo</span></p>
                            <p className="text-[10px] sm:text-xs text-slate-500">Projected value</p>
                          </div>
                        )}
                        {streak > 0 && (
                          <div className="text-center">
                            <p className="text-2xl sm:text-3xl font-black text-amber-400 tabular-nums">{streak}</p>
                            <p className="text-[10px] sm:text-xs text-slate-500">Day streak</p>
                          </div>
                        )}
                      </div>
                      <div className="flex justify-center gap-3">
                        <button onClick={handleShare}
                          className="flex items-center gap-1.5 bg-[#3b82f6]/10 border border-[#3b82f6]/25 text-[#3b82f6] text-xs font-semibold px-4 py-2.5 rounded-full hover:bg-[#3b82f6]/20 transition-colors">
                          <Share2 className="w-3.5 h-3.5" /> Share your results
                        </button>
                      </div>
                    </div>
                  )}

                  {/* ── All skipped mode ── */}
                  {focusTool.mode === 'all_skipped' && (
                    <div className="text-center py-4">
                      <p className="text-slate-300 mb-3">You've skipped all remaining tools. Want to revisit any?</p>
                      <button onClick={() => { setSkippedTools([]); localStorage.removeItem(`skipped_tools_${reportId}`); }}
                        className="text-[#3b82f6] text-sm font-semibold hover:underline">
                        Reset skipped tools →
                      </button>
                    </div>
                  )}

                  {/* ── Value unlocked bar (shown in start/resume) ── */}
                  {(focusTool.mode === 'start' || focusTool.mode === 'resume') && valueUnlocked > 0 && (
                    <div className="mt-4 pt-3 border-t border-white/5 flex items-center justify-between text-xs">
                      <span className="text-slate-500">${valueUnlocked.toLocaleString()}/mo value unlocked so far</span>
                      <span className="text-slate-600">${valueTotal.toLocaleString()}/mo total projected</span>
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            <GuideTab
              phases={phases}
              actionSteps={normalized?.actionSteps || []}
              implementationTimeline={fullReport?.implementationGuide || {}}
              adoptionChecklist={normalized?.adoptionChecklist || []}
              playbookProgress={stepProgress}
              onPlaybookUpdate={(stepId, status) => handleStepCheck(stepId, 0)}
              topProblem={primaryProblems[0] || null}
              topTool={(focusTool?.tool?.name || focusTool?.tool?.tool_name) ?? null}
              topPlaybook={focusToolPlaybook?.name ?? null}
              topPrompt={promptPack[0]?.prompt ?? promptPack[0]?.label ?? null}
              onAskAI={(prompt) => { setAskInput(prompt); handleAskSubmit(null, prompt); }}
              allTools={tools}
              progress={progress?.tools || {}}
            />
          </TabsContent>

          <TabsContent value="tools" forceMount className="data-[state=inactive]:hidden pt-8">
            <ToolsTab
              tools={tools}
              industry={industry}
              subVertical={subVertical}
              businessName={businessName}
              ownerProblems={primaryProblems}
              toolProgress={progress?.tools || {}}
              onProgressUpdate={handleProgressUpdate}
              onCopilot={(prompt) => { setAskInput(prompt); handleAskSubmit(null, prompt); }}
              t={t}
              integrationGraph={fullReport?.integrationGraph}
              matchedProblems={matchedProblems}
              reportId={reportId}
              existingTools={
                String(fullReport?.userResponses?.rawCurrentTools || '')
                  .split(',').map(s => s.trim()).filter(Boolean)
              }
            />
          </TabsContent>

          <TabsContent value="diagnosis" forceMount className="data-[state=inactive]:hidden pt-8">
            <DiagnosisTab
              fullReport={fullReport}
              industry={industry}
              subVertical={subVertical}
              t={t}
              onGoToTool={() => setActiveTab('tools')}
              progress={progress?.tools || {}}
              businessSize={reportData?.businessSize || ''}
            />
          </TabsContent>

          <TabsContent value="financials" forceMount className="data-[state=inactive]:hidden pt-8">
            <FinancialsTab
              financialData={financialData}
              fullReport={fullReport}
              industry={industry}
              subVertical={subVertical}
              t={t}
              progress={progress?.tools || {}}
              totalTools={tools.length}
              onGoToTool={() => setActiveTab('tools')}
            />
          </TabsContent>

          <TabsContent value="prompts" forceMount className="data-[state=inactive]:hidden pt-8">
            <PromptsTab
              promptPack={promptPack}
              copiedPrompt={copiedPrompt}
              copyToClipboard={copyToClipboard}
              reportId={reportId}
              industry={industry}
              subVertical={subVertical}
              promptPackIntro={fullReport?.promptPackIntro}
              assistantLabel={assistantLabel}
              t={t}
            />
          </TabsContent>

          {/* ── More Tabs ── */}
          <TabsContent value="playbooks" forceMount className="data-[state=inactive]:hidden pt-8">
            <PlaybooksTab
              report={fullReport}
              onAskAI={(p) => { setAskInput(p); handleAskSubmit(null, p); }}
              promptPack={promptPack}
              onGoToPrompts={() => setActiveTab('prompts')}
            />
          </TabsContent>

          <TabsContent value="market" forceMount className="data-[state=inactive]:hidden pt-8">
            <MarketTab
              adoptionStats={fullReport?.industryBenchmark || {}}
              toolAdoptionData={fullReport?.aiBenchmarks2025 || {}}
              filteredTrends={fullReport?.freshAISignals || []}
              industry={industry}
              subVertical={subVertical}
              regionalData={{}}
              fullReport={fullReport}
            />
          </TabsContent>

          <TabsContent value="risks" forceMount className="data-[state=inactive]:hidden pt-8">
            <RisksTab risks={normalized?.risks || []} phases={phases} tools={tools} currentPhase={focusPhaseNumber} industry={industry} subVertical={subVertical} />
          </TabsContent>

          <TabsContent value="compliance" forceMount className="data-[state=inactive]:hidden pt-8">
            <ComplianceTab complianceGuide={fullReport?.complianceGuide} industry={industry} subVertical={subVertical} />
          </TabsContent>

          <TabsContent value="competitive" forceMount className="data-[state=inactive]:hidden pt-8">
            <CompetitiveTab industry={industry} subVertical={subVertical} competitiveIntelligence={fullReport?.competitiveIntelligence} fullReport={fullReport} tools={tools} />
          </TabsContent>

        </Tabs>

        {/* ── Feedback (always visible, below tabs) ── */}
        {isPaid && (
          <div className="max-w-4xl mx-auto px-4 py-10">
            <FeedbackSection reportId={reportId} businessName={businessName} industry={industry} doneCount={doneCount} />
          </div>
        )}

        {/* ── Referral ── */}
        {isPaid && (
          <div className="max-w-4xl mx-auto px-4 pb-20">
            <ReferralBlock reportId={reportId} industry={industry} subVertical={subVertical} t={t} />
          </div>
        )}

        {/* ── Sticky bottom CTA ── */}
        <StickyBottomCTA tools={tools} focusTool={focusTool} onTabSwitch={setActiveTab} activeTab={activeTab} doneCount={doneCount} />

        {/* ── Dev strip ── */}
        {process.env.NODE_ENV === 'development' && fullReport?.engineMeta && (
          <div className="max-w-6xl mx-auto px-4 pb-8">
            <div className="px-4 py-2 bg-slate-900 border border-slate-800 rounded-lg text-xs text-slate-600 flex flex-wrap gap-4">
              <span>Engine v{fullReport.engineMeta.report_version}</span>
              <span>Generated in {((fullReport.engineMeta.generation_ms || 0) / 1000).toFixed(1)}s</span>
              {fullReport.engineMeta.top_problem_cluster && <span>Cluster: {fullReport.engineMeta.top_problem_cluster.replace(/_/g, ' ')}</span>}
              <span>ID: {fullReport.engineMeta.report_id || reportId}</span>
            </div>
          </div>
        )}
      </main>

      {/* ── Coach Panel (floating) with first-visit tooltip ── */}
      {showCoachTooltip && (
        <div className="fixed bottom-20 right-6 z-[51] print:hidden">
          <div className="relative bg-slate-900 border border-white/15 rounded-2xl px-4 py-3 shadow-2xl shadow-black/40 max-w-[220px]">
            <p className="text-sm text-white font-semibold leading-snug">
              Hi {businessName?.split(' ')[0] || 'there'} — I've read your full report.
            </p>
            <p className="text-xs text-slate-400 mt-0.5">Ask me anything.</p>
            <div className="absolute -bottom-1.5 right-7 w-3 h-3 bg-slate-900 border-r border-b border-white/15 rotate-45" />
          </div>
        </div>
      )}
      <CoachPanel
        reportId={reportId}
        industry={industry}
        businessName={businessName}
        tools={tools}
        blueprints={blueprints}
        askMessages={askMessages}
        askInput={askInput}
        setAskInput={setAskInput}
        askLoading={askLoading}
        askQuestionsUsed={askQuestionsUsed}
        ASK_LIMIT={ASK_LIMIT}
        onSubmit={handleAskSubmit}
      />

      {/* ── Phase Complete Modal ── */}
      <AnimatePresence>
        {phaseCompleteModal && (
          <motion.div key="phase-complete-backdrop"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center px-4"
            style={{ background: 'rgba(2,6,23,0.85)', backdropFilter: 'blur(6px)' }}
            onClick={() => setPhaseCompleteModal(null)}>
            <motion.div key="phase-complete-card"
              initial={{ scale: 0.85, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 320, damping: 28 }}
              onClick={e => e.stopPropagation()}
              className="bg-slate-900 border border-blue-500/30 rounded-3xl p-8 max-w-sm w-full text-center shadow-2xl">
              <div className="text-4xl mb-4">🎉</div>
              <h2 className="text-2xl font-bold text-white mb-1">Phase {phaseCompleteModal.index + 1} Complete!</h2>
              <p className="text-slate-400 text-sm mb-5">{(phaseCompleteModal.phase.tools || []).length} tools implemented</p>
              {phases[phaseCompleteModal.index + 1] && (
                <div className="bg-slate-950/50 border border-white/10 rounded-2xl p-4 mb-5 text-left">
                  <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-1">Up next</p>
                  <p className="text-white font-semibold">{phases[phaseCompleteModal.index + 1].name}</p>
                  {phases[phaseCompleteModal.index + 1].focus && (
                    <p className="text-slate-400 text-xs mt-0.5">{phases[phaseCompleteModal.index + 1].focus}</p>
                  )}
                </div>
              )}
              <button onClick={() => setPhaseCompleteModal(null)}
                className="w-full bg-gradient-to-br from-[#3b82f6] to-blue-500 text-slate-950 font-bold py-3 rounded-full transition-all hover:scale-[1.02]">
                {phases[phaseCompleteModal.index + 1] ? `Begin Phase ${phaseCompleteModal.index + 2} →` : 'View full blueprint →'}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Win Banner ── */}
      <AnimatePresence>
        {winBanner && (
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 40 }}
            className="fixed bottom-6 left-6 z-50 bg-[#131c30] border border-[#3b82f6]/30 rounded-2xl px-5 py-4 shadow-2xl max-w-xs print:hidden">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-[#3b82f6]/20 flex items-center justify-center">
                <Check size={16} className="text-[#3b82f6]" />
              </div>
              <div>
                <p className="text-sm font-semibold text-white">{winBanner.toolName} — step complete!</p>
                {winBanner.hoursSaved && <p className="text-xs text-slate-400 mt-0.5">~{winBanner.hoursSaved}h/wk saved when fully set up</p>}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default FullReportPage;
